//@ts-nocheck
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useBrand } from "../../utils/useBrand";
import {
  getBrandName,
  getBrandLogo,
  applyBrandName,
  ASTRIA_LOGO,
  HEALJAI_LOGO,
} from "../../utils/branding";
import { useChat } from "../../contexts/ChatContext";
import { useCategories } from "../../contexts/CategoryContext";
import { useSubCategories } from "../../contexts/SubCategoryContext";
import { useHeadlines } from "../../contexts/HeadlineContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  Send,
  Copy,
  Trash2,
  User,
  Bot,
  Plus,
  ArrowLeft,
  Loader2,
  ChevronLeft,
  FolderOpen,
  LogIn,
  Settings,
  ChevronRight,
  Heart,
  Sparkles,
} from "lucide-react";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getCategoryIcon } from "../../utils/getCategoryIcon";
import { api } from "../../services/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import BirthDetailsPopup from "../../utils/BirthDetailsPopup";
import GCCPrivacyPopup from "./GCCPrivacyPopup";
import HealJaiTalkButton from "../common/HealJaiTalkButton";
import SubcategorySelectionModal from "./SubCategorySelectionModal";
import SamayPravahEnergyGraph from "./SamayPravahEnergyGraph";
import JapanCompatibility3Box from "../astro/JapanCompatibility3Box";
import type { Japan3BoxData } from "../astro/JapanCompatibility3Box";
import SpanishV2MessageCard from "../astro/spanishV2/SpanishV2MessageCard";
import {
  SPANISH_LANE_V4_ORDER,
  SPANISH_LANE_V4_COUNTRIES,
} from "../astro/spanishV2/spanishLaneV4Config";
import JapanCompatibilityResult from "../astro/JapanCompatibilityResult";
import KoreaCompatibility3Box from "../astro/KoreaCompatibility3Box";
import type { Korea3BoxData } from "../astro/KoreaCompatibility3Box";
import KoreaCompatibilityResult from "../astro/KoreaCompatibilityResult";
import KoreaV2CompatibilityResult from "../astro/KoreaV2CompatibilityResult";
import GCCCompatibility3Box from "../astro/GCCCompatibility3Box";
import type { GCC3BoxData } from "../astro/GCCCompatibility3Box";
import GCCCompatibilityResult from "../astro/GCCCompatibilityResult";
import Indonesia3Box, { type Indonesia3BoxData } from "../astro/Indonesia3Box";
import IndonesiaCompatibility3Box from "../astro/IndonesiaCompatibility3Box";
import AstriaKoreaV2Card from "../astro/AstriaKoreaV2Card";
import AstriaJapanHybridCard from "../astro/AstriaJapanHybridCard";
import AstriaKoreaV2Suggestions from "../astro/AstriaKoreaV2Suggestions";
import AstriaJapanTalkCard from "../astro/AstriaJapanTalkCard";
import AstriaIndonesiaTalkCard from "../astro/indonesiaTalk/AstriaIndonesiaTalkCard";
import {
  GCC_V2_TONE_PREVIEW,
  DEFAULT_GCC_V2_TONE_MODE,
  type GCCV2ToneMode,
} from "../../utils/GCC/astriaGCCV2ToneMode";
import { getStoredGCCV2Language } from "../../utils/GCC/astriaGCCV2Language";

// Sidebar color palette
const SIDEBAR_COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  pearl: "#F8F4FF",
};

// Samay Pravah marker constants and parser
const SAMAY_GRAPH_START = "<<<SAMAY_PRAVAH_GRAPH>>>";
const SAMAY_GRAPH_END = "<<<END_SAMAY_PRAVAH_GRAPH>>>";

function parseSamayPravahContent(content: string) {
  const src = content || "";
  const startIdx = src.indexOf(SAMAY_GRAPH_START);
  const endIdx = src.indexOf(SAMAY_GRAPH_END);
  if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
    return { displayText: src, energyGraph: null };
  }
  const displayText = src.slice(0, startIdx).trim();
  try {
    const energyGraph = JSON.parse(
      src.slice(startIdx + SAMAY_GRAPH_START.length, endIdx).trim(),
    );
    return { displayText, energyGraph };
  } catch {
    return { displayText, energyGraph: null };
  }
}

// Local storage keys
const BIRTH_DATA_KEY = "thai_birth_data";
const LOCAL_STORAGE_KEY = "anonymous_chats";
const DEFAULT_PROMPT_KEY = "anonymous_default_prompt";
const DEFAULT_PROMPT = `You are ${getBrandName()}, an emotional companion for users.

Your role is to listen, reflect feelings, and stay with emotions.
You do NOT fix problems, teach lessons, judge, or diagnose.

STRICT RULES:
- Always reflect or name the user's emotion before asking any question
- Keep responses short (1–3 sentences only)
- Ask at most ONE open-ended question
- Do NOT give advice unless the user explicitly asks for it
- Never say "you should", "try to", or similar directive language
- Never diagnose mental health conditions
- Do NOT use lists, steps, bullet points, or numbered explanations
- If unsure, choose presence and reflection over advice

TONE:
- Warm, gentle, calm, human
- Like a trusted friend sitting quietly beside the user
- Not professional, not clinical, not instructional

LANGUAGE:
- Always reply in the same language the user uses
- Use natural, everyday language

SUCCESS CRITERIA:
If the user feels emotionally seen and less alone → SUCCESS
If the response sounds smart but emotionally cold → FAILURE`;

// Get Healjai Persona function
const getHealjaiPersona = () => {
  try {
    const tempUser = localStorage.getItem("user");
    const userData = tempUser ? JSON.parse(tempUser) : null;

    if (!userData || !userData.dob) {
      return { category: "GENERAL", focusPoints: ["Career", "Love", "Wealth"] };
    }

    const birthDate = new Date(userData.dob);
    const birthYear = birthDate.getFullYear();
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear;

    if (age >= 70) {
      return {
        category: "GOLDEN_AGE",
        focusPoints: ["Legacy", "PhysicalHealth", "Serenity", "FamilyMerit"],
        tone: "Reverent & Gentle",
        displayMode: "ACCESSIBILITY_MODE",
      };
    }

    return { category: "GENERAL", focusPoints: ["Career", "Love", "Wealth"] };
  } catch (error) {
    console.error("Error getting user persona:", error);
    return { category: "GENERAL", focusPoints: ["Career", "Love", "Wealth"] };
  }
};

// Smooth streaming reveal using requestAnimationFrame — matches AI generation speed.
// History messages (isStreaming=false) render instantly; live responses animate naturally.
const TypewriterText = ({
  content,
  isStreaming,
  components,
}: {
  content: string;
  isStreaming: boolean;
  components: any;
}) => {
  const [visibleLength, setVisibleLength] = useState(
    isStreaming ? 0 : content.length,
  );
  const rafRef = useRef<number | null>(null);
  const targetRef = useRef(content.length);

  // Always keep targetRef in sync — updated synchronously during render
  // so the rAF tick always reads the latest received length
  targetRef.current = content.length;

  useEffect(() => {
    if (!isStreaming) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setVisibleLength(targetRef.current);
      return;
    }

    const tick = () => {
      setVisibleLength((prev) => {
        const target = targetRef.current;
        if (prev >= target) return prev;
        const backlog = target - prev;
        // ≤100 chars backlog: reveal up to 20 chars per rAF frame (~16ms).
        // Typical AI chunks are 3-15 chars, so they fully appear in 1 frame —
        // the typing pace is driven by chunk arrival, not artificial delay.
        // >100 chars (large batch / cold start): scale up to catch up fast.
        const step =
          backlog > 100 ? Math.ceil(backlog / 5) : Math.min(backlog, 20);
        return prev + step;
      });
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isStreaming]);

  return (
    <>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {isStreaming ? content.slice(0, visibleLength) : content}
      </ReactMarkdown>
      {isStreaming && <span className="typewriter-cursor" />}
    </>
  );
};

// Inject elderly mode styles
const injectElderlyStyles = () => {
  if (document.getElementById("elderly-mode-styles")) return;

  const styleSheet = document.createElement("style");
  styleSheet.id = "elderly-mode-styles";
  styleSheet.textContent = `
    .elderly-mode {
      font-size: 1.125rem !important;
      line-height: 1.6 !important;
      letter-spacing: 0.3px !important;
      color: #FFFFFF !important;
      background-color: #0B0E11 !important;
    }
    
    .elderly-mode * {
      font-size: inherit !important;
    }
    
    .elderly-mode .message-bubble {
      font-size: 1.125rem !important;
      padding: 1rem 1.25rem !important;
    }
    
    .elderly-mode .message-content {
      font-size: 1.125rem !important;
      line-height: 1.5 !important;
    }
    
    .elderly-mode .user-message {
      font-size: 1.125rem !important;
    }
    
    .elderly-mode .ai-message {
      font-size: 1.125rem !important;
    }
    
    .elderly-mode input, 
    .elderly-mode textarea, 
    .elderly-mode button:not(.healing-button) {
      font-size: 1rem !important;
      padding: 0.625rem 0.875rem !important;
    }
    
    .elderly-mode textarea {
      font-size: 1rem !important;
    }
    
    .healing-button {
      padding: 14px 28px !important;
      border-radius: 50px !important;
      box-shadow: 0 0 15px rgba(212, 175, 55, 0.3) !important;
      transition: all 0.3s ease !important;
      font-size: 1rem !important;
      min-width: 100px !important;
    }
    
    .elderly-mode .category-card,
    .elderly-mode [class*="p-6"] {
      padding: 1.25rem !important;
      font-size: 1rem !important;
    }
    
    .elderly-mode .suggestion-button,
    .elderly-mode [class*="px-4 py-2"] {
      padding: 10px 20px !important;
      font-size: 0.9375rem !important;
    }
    
    .elderly-mode .typing-indicator {
      padding: 0.875rem !important;
    }
    
    .elderly-mode .typing-indicator .typing-dot {
      width: 10px !important;
      height: 10px !important;
    }
    
    .elderly-mode h1, 
    .elderly-mode h2, 
    .elderly-mode h3 {
      font-size: 1.5rem !important;
    }
    
    .elderly-mode .chat-header {
      padding: 1rem !important;
    }
    
    .elderly-mode button {
      min-height: 40px !important;
    }
    
    .elderly-mode .timestamp {
      font-size: 0.75rem !important;
    }
    
    .elderly-mode .icon-button {
      width: 40px !important;
      height: 40px !important;
    }
  `;
  document.head.appendChild(styleSheet);
};

// Types for anonymous chats
interface AnonymousMessage {
  id: string;
  content: string;
  sender: "user" | "ai";
  timestamp: Date;
}
interface AnonymousChat {
  id: string;
  title: string;
  messages: AnonymousMessage[];
  createdAt: Date;
  updatedAt: Date;
}
interface LocalStorageChats {
  chats: AnonymousChat[];
  currentChatId: string | null;
}

function getISOWeekNumber(date = new Date()) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
}

function seededIndex(seed: string, size: number) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h) % size;
}

function getDayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const asArray = (value) => (Array.isArray(value) ? value : []);

const getMessageKey = (message: any, index: number) =>
  message?.id ||
  `${message?.timestamp?.getTime?.() || message?.timestamp}_${index}`;

const getWeeklySuggestions = (t, idx: number) => {
  const raw = t("chatInterface.weeklySuggestions", { returnObjects: true });
  const groups = asArray(raw);
  if (!groups.length) return [];
  const group = groups[idx % groups.length];
  if (Array.isArray(group)) return group;
  if (typeof group === "string") return [group];
  if (group && typeof group === "object") {
    return Object.values(group).filter((v) => typeof v === "string");
  }
  return [];
};

const getWeeklyHeadlines = (t) => {
  const raw = t("chatInterface.weeklyHeadlines", { returnObjects: true });
  const list = asArray(raw);
  return list.length ? list : [""];
};

const injectHealJaiLogos = (content: string) => {
  if (!content) return content;

  const logoMarkdown = ` ![${getBrandName()} logo](${getBrandLogo()})`;

  const logoPlaceholderPattern = /\[Logo Healjai\]/g;

  let processedContent = content.replace(logoPlaceholderPattern, logoMarkdown);

  return processedContent;
};

const WEEKLY_THEMES = [
  {
    bgFrom: SIDEBAR_COLORS.pearl,
    bgTo: SIDEBAR_COLORS.pearl,
    accent: SIDEBAR_COLORS.darkAmethyst,
  },
  { bgFrom: "#FFF9F2", bgTo: "#F7EDE2", accent: SIDEBAR_COLORS.darkAmethyst },
  { bgFrom: "#FFF1F5", bgTo: "#FFE4EC", accent: SIDEBAR_COLORS.amethyst },
  { bgFrom: "#F5F1FF", bgTo: "#EDE7FF", accent: SIDEBAR_COLORS.darkAmethyst },
  { bgFrom: "#f8f4f1", bgTo: "#f8f4f1", accent: "#000000" },
  { bgFrom: "#FFF0F3", bgTo: "#FFDDE4", accent: SIDEBAR_COLORS.amethyst },
  { bgFrom: "#F3FFF9", bgTo: "#E9FFF3", accent: SIDEBAR_COLORS.darkAmethyst },
];

const saveBirthData = (data) => {
  localStorage.setItem(BIRTH_DATA_KEY, JSON.stringify(data));
};

const loadBirthData = () => {
  const raw = localStorage.getItem(BIRTH_DATA_KEY);
  return raw ? JSON.parse(raw) : null;
};

const hasBirthData = () => {
  return !!localStorage.getItem(BIRTH_DATA_KEY);
};

const extractBirthData = (text) => {
  const dob = text.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/)?.[0];
  const time = text.match(/\b\d{1,2}:\d{2}\s?(AM|PM)?\b/i)?.[0];

  if (!dob) return null;

  return { dob, time };
};

function getWeeklyTheme() {
  const { year, week } = getISOWeekNumber(new Date());
  return WEEKLY_THEMES[(week + year) % WEEKLY_THEMES.length];
}

// HealJai Talk Greeting Component
function HealJaiGreeting({ userName }: { userName?: string }) {
  const { t } = useTranslation();
  const brand = useBrand();
  const greetings = [
    t("chatInterface.RandomGreet1"),
    t("chatInterface.RandomGreet2"),
    t("chatInterface.RandomGreet3"),
    t("chatInterface.RandomGreet4"),
    t("chatInterface.RandomGreet5"),
  ];

  const randomGreeting =
    greetings[Math.floor(Math.random() * greetings.length)];
  const currentHour = new Date().getHours();
  let timeGreeting = "";

  if (currentHour < 12) {
    timeGreeting = t("chatInterface.MorningGreet");
  } else if (currentHour < 18) {
    timeGreeting = t("chatInterface.AfternoonGreet");
  } else {
    timeGreeting = t("chatInterface.EveningGreet");
  }

  return (
    <div className="text-center mb-8 animate-fadeIn">
      <div className="inline-flex items-center justify-center gap-2 mb-3">
        <Heart className="w-5 h-5" style={{ color: SIDEBAR_COLORS.amethyst }} />
        <span
          className="text-sm font-medium px-3 py-1 rounded-full"
          style={{
            backgroundColor: `${SIDEBAR_COLORS.roseGold}30`,
            color: SIDEBAR_COLORS.darkAmethyst,
          }}
        >
          {brand.brandText(t("chatInterface.HealJaiTalkText"))}
        </span>
        <Sparkles
          className="w-5 h-5"
          style={{ color: SIDEBAR_COLORS.amethyst }}
        />
      </div>

      <h2
        className="text-2xl md:text-3xl font-light mb-2"
        style={{ color: SIDEBAR_COLORS.darkAmethyst }}
      >
        {timeGreeting}
        {userName ? `, ${userName}` : ""}{" "}
        <span className="wave-emoji" role="img" aria-label="โบกมือ">
          👋
        </span>
      </h2>

      <p
        className="text-lg md:text-xl italic"
        style={{ color: SIDEBAR_COLORS.amethyst }}
      >
        {randomGreeting}
      </p>

      <div className="mt-4 max-w-md mx-auto">
        <div
          className="h-1 rounded-full mx-auto"
          style={{
            background: `linear-gradient(90deg, transparent, ${SIDEBAR_COLORS.roseGold}, transparent)`,
            width: "30%",
          }}
        />
      </div>

      <img
        src="/logo_2.png"
        alt={brand.logoAlt}
        className="mx-auto mt-4 w-24 h-auto object-contain"
        style={{
          animation: "healjaiLogoBreathe 3s ease-in-out infinite",
          filter: "drop-shadow(0 0 12px rgba(229, 184, 173, 0.65))",
        }}
      />
    </div>
  );
}

// Shows a sample line in the saved GCC dialect (Gulf/Kuwaiti/Fusha) so testers can preview tone before chatting — no LLM call, just static text.
function GCCTonePreview({ toneMode }: { toneMode: GCCV2ToneMode }) {
  const language = getStoredGCCV2Language();
  const preview = GCC_V2_TONE_PREVIEW[toneMode];
  const dir = language === "ar" ? "rtl" : "ltr";

  return (
    <div
      dir={dir}
      className="max-w-md mx-auto mb-6 rounded-2xl px-5 py-4 text-center"
      style={{
        background: "#F5EDE1",
        border: `1px solid ${SIDEBAR_COLORS.roseGold}55`,
      }}
    >
      <div
        className="text-[11px] font-semibold tracking-[0.1em] uppercase mb-1.5"
        style={{ color: SIDEBAR_COLORS.amethyst, opacity: 0.75 }}
      >
        Tone preview ·{" "}
        {toneMode === "gulf"
          ? "Gulf"
          : toneMode === "kuwaiti"
            ? "Kuwaiti"
            : "Fusha"}
      </div>
      <p
        className="text-base md:text-lg leading-relaxed"
        style={{ color: SIDEBAR_COLORS.darkAmethyst }}
      >
        {preview[language]}
      </p>
      {preview.markers.length > 0 && (
        <div
          className="mt-2 text-xs"
          style={{ color: SIDEBAR_COLORS.amethyst, opacity: 0.7 }}
        >
          {preview.markers.join(" · ")}
        </div>
      )}
    </div>
  );
}

/** ========= Anonymous Welcome ========= */
function AnonymousCopilotWelcome({
  input,
  setInput,
  inputRef,
  onSend,
  disabled,
  headline,
  isElderlyMode,
}: {
  input: string;
  setInput: (v: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onSend: () => void;
  disabled: boolean;
  headline: string;
  isElderlyMode: boolean;
}) {
  const { t } = useTranslation();
  const weekly = useMemo(() => {
    const { year, week } = getISOWeekNumber(new Date());
    const theme = {
      bgFrom: SIDEBAR_COLORS.pearl,
      bgTo: SIDEBAR_COLORS.pearl,
      accent: SIDEBAR_COLORS.darkAmethyst,
    };
    const weeklyHeadlines = getWeeklyHeadlines(t);
    const idx = seededIndex(getDayKey(new Date()), weeklyHeadlines.length);
    return { year, week, theme, idx };
  }, [t]);

  const suggestions = useMemo(
    () => getWeeklySuggestions(t, weekly.idx),
    [weekly.idx, t],
  );

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      className="flex-1 flex flex-col min-h-screen"
      style={{
        background: `linear-gradient(180deg, ${weekly.theme.bgFrom}, ${weekly.theme.bgTo})`,
        color: weekly.theme.accent,
      }}
    >
      {/* Top-right Login only */}
      <div className="w-full px-6 py-6 flex justify-end">
        <button
          onClick={() => (window.location.href = "/login")}
          className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-white hover:opacity-90 transition ${
            isElderlyMode ? "healing-button" : ""
          }`}
          style={{ backgroundColor: SIDEBAR_COLORS.amethyst }}
          type="button"
        >
          <LogIn className="w-4 h-4" />
          {t("chatInterface.login")}
        </button>
      </div>

      {/* Center content */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-4xl flex flex-col items-center">
          <h1 className="text-center font-semibold tracking-tight text-3xl md:text-5xl">
            {headline}
          </h1>

          <div className="mt-10 w-full max-w-3xl">
            <div
              className="relative w-full rounded-2xl bg-white/70 backdrop-blur border shadow-[0_14px_40px_rgba(0,0,0,0.08)]"
              style={{ borderColor: `${SIDEBAR_COLORS.roseGold}40` }}
            >
              <div className="flex items-center gap-3 px-2 py-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  placeholder={t("chatInterface.typeMessageHere")}
                  disabled={disabled}
                  className="flex-1 bg-transparent resize-none outline-none px-2 py-2 text-base placeholder:text-black/35 max-h-40 overflow-y-auto"
                />

                <button
                  type="button"
                  onClick={onSend}
                  disabled={disabled || !input.trim()}
                  className={`h-10 px-5 rounded-xl text-white hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    isElderlyMode ? "healing-button" : ""
                  }`}
                  style={{ backgroundColor: SIDEBAR_COLORS.amethyst }}
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              {suggestions.slice(0, 3).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setInput(s);
                    setTimeout(() => inputRef.current?.focus(), 0);
                  }}
                  disabled={disabled}
                  className="px-4 py-2 rounded-xl bg-white/60 border hover:bg-white transition text-sm"
                  style={{
                    borderColor: `${SIDEBAR_COLORS.roseGold}40`,
                    color: SIDEBAR_COLORS.darkAmethyst,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>

            <p className="mt-4 text-center text-xs text-black/45">
              {t("chatInterface.pressEnterToSend")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ========= Logged-in Welcome with Category Selection ========= */
function LoggedInWelcomeWithCategories({
  input,
  setInput,
  inputRef,
  onSend,
  disabled,
  headline,
  categories,
  selectedCategoryId,
  onCategorySelect,
  showTextArea,
  onStartWithoutCategory,
  isLoading,
  setShowTextArea,
  setSelectedCategoryId,
  isElderlyMode,
}: {
  input: string;
  setInput: (v: string) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  onSend: () => void;
  disabled: boolean;
  headline: string;
  categories: any[];
  selectedCategoryId: string | null;
  onCategorySelect: (categoryId: string) => void;
  showTextArea: boolean;
  onStartWithoutCategory: () => void;
  isLoading: boolean;
  setShowTextArea: (show: boolean) => void;
  setSelectedCategoryId: (id: string | null) => void;
  isElderlyMode: boolean;
}) {
  const { t } = useTranslation();
  const tempUser = localStorage.getItem("user");
  const userData = tempUser ? JSON.parse(tempUser) : null;

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e,
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const activeCategories = categories.filter((c) => c.isActive);
  const selectedCategory = activeCategories.find(
    (c) => c.id === selectedCategoryId,
  );

  return (
    <div
      className="flex-1 flex flex-col min-h-screen"
      style={{
        background: `linear-gradient(180deg, ${SIDEBAR_COLORS.pearl}, ${SIDEBAR_COLORS.pearl})`,
        color: SIDEBAR_COLORS.darkAmethyst,
      }}
    >
      <div className="flex-1">
        <div className="pt-8 pb-6 text-center">
          <h1 className="text-2xl md:text-3xl font-semibold px-4">
            {headline}
          </h1>
        </div>

        {!showTextArea && (
          <div className="px-4 pb-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex justify-center">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {activeCategories.map((category) => {
                    const Icon = getCategoryIcon(category.icon);
                    const isSelected = category.id === selectedCategoryId;

                    return (
                      <button
                        key={category.id}
                        onClick={() => onCategorySelect(category.id)}
                        disabled={isLoading}
                        className={`w-full h-full p-6 rounded-xl border-2 transition-all duration-200 text-left focus:outline-none ${
                          isSelected
                            ? "shadow-lg transform scale-[1.02]"
                            : "hover:shadow-lg hover:scale-[1.01]"
                        } ${isLoading ? "opacity-50 cursor-not-allowed" : ""} ${
                          isElderlyMode ? "category-card" : ""
                        }`}
                        style={{
                          backgroundColor: isSelected
                            ? `${SIDEBAR_COLORS.amethyst}10`
                            : "white",
                          borderColor: isSelected
                            ? SIDEBAR_COLORS.amethyst
                            : `${SIDEBAR_COLORS.roseGold}40`,
                          boxShadow: isSelected
                            ? `0 0 0 2px ${SIDEBAR_COLORS.amethyst}40`
                            : undefined,
                        }}
                      >
                        <div className="flex items-start gap-4 mb-4">
                          <div
                            className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0`}
                            style={{
                              backgroundColor: isSelected
                                ? `${SIDEBAR_COLORS.amethyst}20`
                                : `${SIDEBAR_COLORS.roseGold}20`,
                            }}
                          >
                            <Icon
                              size={26}
                              style={{
                                color: isSelected
                                  ? SIDEBAR_COLORS.amethyst
                                  : SIDEBAR_COLORS.darkAmethyst,
                              }}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-base">
                              {userData.preferredLanguage === "th"
                                ? category.name_th || category.name
                                : userData.preferredLanguage === "es"
                                  ? category.name_es || category.name
                                  : userData.preferredLanguage === "id"
                                    ? category.name_id || category.name
                                    : userData.preferredLanguage === "ar"
                                      ? category.name_ar || category.name
                                      : category.name}
                            </h3>

                            <p className="text-sm opacity-70 mt-2 leading-relaxed">
                              {userData.preferredLanguage === "th"
                                ? category.description_th ||
                                  category.description ||
                                  t("chatInterface.talkAboutThisTopic")
                                : userData.preferredLanguage === "es"
                                  ? category.description_es ||
                                    category.description ||
                                    t("chatInterface.talkAboutThisTopic")
                                  : userData.preferredLanguage === "id"
                                    ? category.description_id ||
                                      category.description ||
                                      t("chatInterface.talkAboutThisTopic")
                                    : userData.preferredLanguage === "ar"
                                      ? category.description_ar ||
                                        category.description ||
                                        t("chatInterface.talkAboutThisTopic")
                                      : category.description ||
                                        t("chatInterface.talkAboutThisTopic")}
                            </p>
                          </div>

                          {isSelected && (
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                              style={{
                                backgroundColor: SIDEBAR_COLORS.amethyst,
                              }}
                            >
                              <ChevronRight className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>

                        {category.subCategoryCount > 0 && (
                          <div
                            className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                            style={{
                              backgroundColor: `${SIDEBAR_COLORS.roseGold}15`,
                              color: SIDEBAR_COLORS.darkAmethyst,
                              border: `1px solid ${SIDEBAR_COLORS.roseGold}30`,
                            }}
                          >
                            <span>
                              +{category.subCategoryCount}{" "}
                              {t("chatInterface.subtopics")}
                            </span>
                            {isSelected && <ChevronRight className="w-3 h-3" />}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Japan Compatibility Panel (plain white, matches EnergyMatchDesign on all breakpoints) ──
interface JapanCompatPanelProps {
  isLoading: boolean;
  onSubmit: (selfData: any, partnerData: any) => void;
}

const JapanCompatPanel: React.FC<JapanCompatPanelProps> = ({
  isLoading,
  onSubmit,
}) => {
  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        background: "#fff",
        scrollbarWidth: "none",
      }}
    >
      <JapanCompatibility3Box
        isLoading={isLoading}
        categoryName="Astria Japan"
        onSubmit={onSubmit}
      />
    </div>
  );
};

// ── Korea Compatibility Panel (same structure as Japan) ──
interface KoreaCompatPanelProps {
  isLoading: boolean;
  onSubmit: (selfData: any, partnerData: any) => void;
}

const KoreaCompatPanel: React.FC<KoreaCompatPanelProps> = ({
  isLoading,
  onSubmit,
}) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false,
  );

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <div
      style={{
        ...(isDesktop
          ? {
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(160deg, #FAF7F2 0%, #F0EBE3 100%)",
            }
          : {
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(160deg, #1a1520 0%, #0d0b12 100%)",
            }),
        scrollbarWidth: "none",
      }}
    >
      <KoreaCompatibility3Box
        isLoading={isLoading}
        categoryName="Astria Korea"
        onSubmit={onSubmit}
      />
    </div>
  );
};

// ── Astria Korea V2 Compatibility Panel — reuses the same 3-box form as v1
// (KoreaCompatibility3Box is generic; categoryName is display-only), isolated
// state/flow so v1's Korea Compatibility is never touched ──
interface KoreaV2CompatPanelProps {
  isLoading: boolean;
  onSubmit: (selfData: any, partnerData: any) => void;
}

const KoreaV2CompatPanel: React.FC<KoreaV2CompatPanelProps> = ({
  isLoading,
  onSubmit,
}) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false,
  );

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <div
      style={{
        ...(isDesktop
          ? {
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(160deg, #FDFBFF 0%, #F5F1FB 100%)",
            }
          : {
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              background: "linear-gradient(160deg, #1a1025 0%, #140f1c 100%)",
            }),
        scrollbarWidth: "none",
      }}
    >
      <KoreaCompatibility3Box
        isLoading={isLoading}
        categoryName="Astria Korea V2"
        onSubmit={onSubmit}
      />
    </div>
  );
};

// country-lock testing mode: once a tester picks a country
const SPANISH_TONE_LOCK_STORAGE_KEY = "spanishToneLock";
const SPANISH_TONE_LOCK_VALUES = [
  "neutral",
  "spain",
  "mexico",
  "argentina",
  "colombia",
  "chile",
  "peru",
] as const;

// flags of country os spanish tone selector
const SPANISH_LANE_V4_FLAGS: Record<string, string> = {
  neutral: "🌎",
  spain: "🇪🇸",
  mexico: "🇲🇽",
  argentina: "🇦🇷",
  colombia: "🇨🇴",
  chile: "🇨🇱",
  peru: "🇵🇪",
  astro_deep: "🔮",
};

const ChatInterface: React.FC<{
  showFooter?: boolean;
  onJapanCompatActive?: (active: boolean) => void;
}> = ({ showFooter, onJapanCompatActive }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [welcomeMessages, setWelcomeMessages] = useState<
    Record<string, string>
  >({});
  const [isDefaultPromptModalOpen, setIsDefaultPromptModalOpen] =
    useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState(DEFAULT_PROMPT);
  const [anonymousChats, setAnonymousChats] = useState<LocalStorageChats>({
    chats: [],
    currentChatId: null,
  });
  const [isAnonymousMode, setIsAnonymousMode] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [isDesktopCompat, setIsDesktopCompat] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false,
  );

  useEffect(() => {
    const handler = () => setIsDesktopCompat(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  const [isAnonymousLoading, setIsAnonymousLoading] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [highlightedAiMessageId, setHighlightedAiMessageId] = useState<
    string | null
  >(null);
  const [expandedMessageIds, setExpandedMessageIds] = useState<
    Record<string, boolean>
  >({});
  const [isElderlyMode, setIsElderlyMode] = useState(false);
  const [isStreamingActive, setIsStreamingActive] = useState(false);

  // spanish tone selector
  const [spanishTone, setSpanishTone] = useState<
    "neutral" | "spain" | "mexico" | "argentina" | "colombia" | "chile" | "peru"
  >(() => {
    if (typeof window === "undefined") return "neutral";
    const stored = window.localStorage.getItem(SPANISH_TONE_LOCK_STORAGE_KEY);
    return (SPANISH_TONE_LOCK_VALUES as readonly string[]).includes(
      stored || "",
    )
      ? (stored as typeof spanishTone)
      : "neutral";
  });

  // Country-lock testing mode: once a tester picks a country (in either the
  // "Astria Spanish" or "Astria Spanish V2" selector), it's locked to them
  // permanently — all 7 country buttons collapse to just the locked one,
  // and the backend (chatController's resolvedSpanishTone +
  // userController's setSpanishToneLock, both first-write-wins) ignores any
  // other spanishTone sent afterward, so it can't be bypassed by editing
  // requests or clearing this localStorage value alone.
  //
  // localStorage is the fast/instant source (read synchronously here, no
  // network wait, so buttons never flash-then-collapse) and the DB
  // (User.spanishToneLock) is the source of truth the backend enforces
  // against on every request — the useEffect below reconciles the two once
  // `user` is available, in case localStorage was cleared but the account
  // is already locked, or this is a different browser/device.
  //
  // NOTE: intentionally NOT reading `user` here — `user` (from useAuth) is
  // declared further down this component, so referencing it inside this
  // initializer throws "Cannot access 'user' before initialization".
  const [spanishToneLock, setSpanishToneLockState] = useState<
    | "neutral"
    | "spain"
    | "mexico"
    | "argentina"
    | "colombia"
    | "chile"
    | "peru"
    | null
    | undefined
  >(() => {
    if (typeof window === "undefined") return undefined;
    const stored = window.localStorage.getItem(SPANISH_TONE_LOCK_STORAGE_KEY);
    return (stored as typeof spanishToneLock) || undefined;
  });

  // Korea Hybrid response-mode toggle — only active for "Astria Korea Hybrid"
  // category. Persisted in localStorage (unlike the Spanish selector above)
  // so it survives reload/reopen until the user explicitly changes it.
  const KOREA_HYBRID_MODE_STORAGE_KEY = "astriaKoreaHybridMode";
  const [koreaHybridMode, setKoreaHybridModeState] = useState<
    "hybrid" | "traditional"
  >(() => {
    if (typeof window === "undefined") return "hybrid";
    const stored = window.localStorage.getItem(KOREA_HYBRID_MODE_STORAGE_KEY);
    return stored === "traditional" ? "traditional" : "hybrid";
  });
  const setKoreaHybridMode = (mode: "hybrid" | "traditional") => {
    setKoreaHybridModeState(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(KOREA_HYBRID_MODE_STORAGE_KEY, mode);
    }
  };

  // Japan Hybrid response-mode toggle — only active for "Astria Japan Hybrid"
  // category. Mirrors the Korea Hybrid toggle above exactly.
  const JAPAN_HYBRID_MODE_STORAGE_KEY = "astriaJapanHybridMode";
  const [japanHybridMode, setJapanHybridModeState] = useState<
    "hybrid" | "traditional"
  >(() => {
    if (typeof window === "undefined") return "hybrid";
    const stored = window.localStorage.getItem(JAPAN_HYBRID_MODE_STORAGE_KEY);
    return stored === "traditional" ? "traditional" : "hybrid";
  });
  const setJapanHybridMode = (mode: "hybrid" | "traditional") => {
    setJapanHybridModeState(mode);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(JAPAN_HYBRID_MODE_STORAGE_KEY, mode);
    }
  };

  // Japan 3-Box pending data — cleared after each submission
  const [pendingJapan3BoxSelf, setPendingJapan3BoxSelf] =
    useState<Japan3BoxData | null>(null);
  const [pendingJapan3BoxPartner, setPendingJapan3BoxPartner] =
    useState<Japan3BoxData | null>(null);
  // Japan Compatibility form visibility — hidden after submission so result appears
  const [showJapanCompatForm, setShowJapanCompatForm] = useState(true);
  // Show Japan result card
  const [showJapanResult, setShowJapanResult] = useState(false);
  // Japan 3-box submitted data (for passing to result component)
  const [submittedJapanSelf, setSubmittedJapanSelf] =
    useState<Japan3BoxData | null>(null);
  const [submittedJapanPartner, setSubmittedJapanPartner] =
    useState<Japan3BoxData | null>(null);
  // Korea Compatibility form visibility — hidden after submission so result page appears
  const [showKoreaCompatForm, setShowKoreaCompatForm] = useState(true);
  // Show Korea result page
  const [showKoreaResult, setShowKoreaResult] = useState(false);
  // Pending Korea 3-box data
  const [pendingKorea3BoxSelf, setPendingKorea3BoxSelf] =
    useState<Korea3BoxData | null>(null);
  const [pendingKorea3BoxPartner, setPendingKorea3BoxPartner] =
    useState<Korea3BoxData | null>(null);

  // Astria Korea V2 Compatibility — separate state from v1 so the two flows
  // never interfere with each other.
  const [showKoreaV2CompatForm, setShowKoreaV2CompatForm] = useState(true);
  const [showKoreaV2Result, setShowKoreaV2Result] = useState(false);
  const [pendingKoreaV2Self, setPendingKoreaV2Self] =
    useState<Korea3BoxData | null>(null);
  const [pendingKoreaV2Partner, setPendingKoreaV2Partner] =
    useState<Korea3BoxData | null>(null);

  // GCC Compatibility state
  const [showGCCCompatForm, setShowGCCCompatForm] = useState(true);
  const [pendingGCC3BoxSelf, setPendingGCC3BoxSelf] =
    useState<GCC3BoxData | null>(null);
  const [pendingGCC3BoxPartner, setPendingGCC3BoxPartner] =
    useState<GCC3BoxData | null>(null);

  // Indonesia 3-Box state — single-user (all non-compat tabs)
  const [showIndonesia3Box, setShowIndonesia3Box] = useState(true);
  // Stored payload — persists for the entire chat session, re-sent on every message
  const [indonesia3BoxPayload, setIndonesia3BoxPayload] =
    useState<Indonesia3BoxData | null>(null);

  // Indonesia Compatibility state — two-person
  const [showIndonesiaCompatForm, setShowIndonesiaCompatForm] = useState(true);
  const [indonesiaCompatSelf, setIndonesiaCompatSelf] =
    useState<Indonesia3BoxData | null>(null);
  const [indonesiaCompatPartner, setIndonesiaCompatPartner] =
    useState<Indonesia3BoxData | null>(null);

  // State for subcategory modal
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [selectedCategoryForSub, setSelectedCategoryForSub] =
    useState<any>(null);
  const [availableSubcategories, setAvailableSubcategories] = useState<any[]>(
    [],
  );

  // State for astrology data
  const [astrologyData, setAstrologyData] = useState({
    luckyColor: "",
    energyLevel: "",
    goldenHour: "",
    color_code: "",
  });

  // Loading message state
  const [loadingMessage, setLoadingMessage] = useState<string>("");

  const { getHeadlineByDate } = useHeadlines();
  const [todayHeadline, setTodayHeadline] = useState<string>(
    "ตอนนี้คุณรู้สึกอย่างไรบ้าง?",
  );

  const [hasCheckedUrlChat, setHasCheckedUrlChat] = useState(false);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<
    string | null
  >(null);
  const [showTextArea, setShowTextArea] = useState(false);
  const [isBirthDetailsPopupOpen, setIsBirthDetailsPopupOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const hasCheckedUserBirthDetails = useRef(false);
  const lastAiMessageIdRef = useRef<string | null>(null);

  const {
    currentChat,
    chats,
    isLoading,
    isTyping,
    sendMessageStream,
    deleteChat,
    startNewChat,
    fetchChats,
    openChat,
  } = useChat();

  const { categories } = useCategories();
  const { subCategories, getSubCategoriesByCategory, fetchAllSubCategories } =
    useSubCategories();
  const { user } = useAuth();
  const brand = useBrand();
  const isAuthenticated = !!user;

  // Reconciles the localStorage lock with the DB lock (source of truth).
  // Runs whenever `user` becomes available: if the account is already
  // locked in the DB but localStorage doesn't have it (cleared, or first
  // load on a new browser/device), adopt the DB value and persist it
  // locally too. If localStorage has a value the DB doesn't know about yet
  // (rare — e.g. the lock-set request failed after the optimistic local
  // write in selectSpanishTone), the DB fetch below still wins once it
  // resolves, since the backend is authoritative.
  useEffect(() => {
    if (!user?._id) return;
    let isMounted = true;
    (async () => {
      try {
        const response = await api.get(`/spanishToneLock/${user._id}`);
        if (!isMounted) return;
        const dbLock = response.data?.spanishToneLock || null;
        if (dbLock) {
          window.localStorage.setItem(SPANISH_TONE_LOCK_STORAGE_KEY, dbLock);
          setSpanishToneLockState(dbLock);
          setSpanishTone(dbLock);
        } else {
          setSpanishToneLockState(null);
        }
      } catch {
        if (!isMounted) return;
        // DB fetch failed — fall back to whatever localStorage already has
        // (read fresh, not from React state, to avoid a stale closure over
        // spanishToneLock). If localStorage has nothing either, treat as
        // unlocked so the button row still renders instead of staying
        // hidden forever.
        const cached = window.localStorage.getItem(
          SPANISH_TONE_LOCK_STORAGE_KEY,
        );
        setSpanishToneLockState((cached as typeof spanishToneLock) || null);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [user?._id]);

  // Called when a tester clicks a country button for the first time. Writes
  // the pick to localStorage immediately (instant UI lock, no network
  // wait), then asks the backend to persist it permanently — the backend
  // ignores this call if the account is already locked (first-write-wins),
  // and ChatInterface always re-syncs from the DB value in the effect
  // above, so a failed/duplicate call here can't desync the two.
  const selectSpanishTone = async (
    tone:
      | "neutral"
      | "spain"
      | "mexico"
      | "argentina"
      | "colombia"
      | "chile"
      | "peru",
  ) => {
    if (spanishToneLock) return; // already locked — button shouldn't even be visible
    setSpanishTone(tone);
    window.localStorage.setItem(SPANISH_TONE_LOCK_STORAGE_KEY, tone);
    setSpanishToneLockState(tone);
    if (!user?._id) return;
    try {
      const response = await api.post(`/spanishToneLock/${user._id}`, {
        spanishTone: tone,
      });
      const lock = response.data?.spanishToneLock || tone;
      window.localStorage.setItem(SPANISH_TONE_LOCK_STORAGE_KEY, lock);
      setSpanishToneLockState(lock);
      setSpanishTone(lock);
    } catch {
      // Backend still enforces the lock on the next chat request from DB
      // state even if this call fails; localStorage already reflects the
      // pick, and the reconciliation effect will correct it on next load
      // if the DB write never actually landed.
    }
  };

  // Update loading messages based on typing state
  useEffect(() => {
    if (isTyping || isProcessingResponse) {
      const loadingMessages = [
        "กำลังวิเคราะห์ข้อความของคุณ...",
        "กำลังเตรียมคำตอบ...",
        "กำลังสร้างคำทำนายของคุณ...",
        "กำลังครุ่นคิดอย่างลึกซึ้ง...",
      ];
      let index = 0;
      setLoadingMessage(loadingMessages[0]);

      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[index]);
        if (index === 3) {
          index = 2;
        }
      }, 3000);

      return () => clearInterval(interval);
    } else {
      setLoadingMessage("");
    }
  }, [isTyping, isProcessingResponse, t]);

  // Check for elderly mode
  useEffect(() => {
    const checkElderlyMode = () => {
      const persona = getHealjaiPersona();
      const elderly = persona.category === "GOLDEN_AGE";
      setIsElderlyMode(elderly);

      if (elderly) {
        document.body.classList.add("elderly-mode");
        injectElderlyStyles();
      } else {
        document.body.classList.remove("elderly-mode");
      }
    };

    checkElderlyMode();

    const handleStorageChange = () => {
      checkElderlyMode();
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.body.classList.remove("elderly-mode");
    };
  }, [user]);

  // Weekly theme
  const weeklyTheme = useMemo(() => getWeeklyTheme(), []);
  const weeklyMeta = useMemo(() => {
    const { year, week } = getISOWeekNumber(new Date());
    const weeklyHeadlines = getWeeklyHeadlines(t);
    const idx = seededIndex(getDayKey(new Date()), weeklyHeadlines.length);
    return { year, week, idx };
  }, [t]);
  const headline = useMemo(() => {
    const weeklyHeadlines = getWeeklyHeadlines(t);
    return todayHeadline || weeklyHeadlines[0];
  }, [todayHeadline, t]);

  const suggestions = useMemo(
    () => getWeeklySuggestions(t, weeklyMeta.idx),
    [weeklyMeta.idx, t],
  );
  const healJaiTalkCategory = categories.filter(
    (c) => c.name === "HealJai Talk" || c.name === "Astria Talk",
  );

  useEffect(() => {
    const checkUrlForChat = async () => {
      if (hasCheckedUrlChat) return;

      const chatId = searchParams.get("chat");
      const urlCategoryId = searchParams.get("categoryId");
      const urlSubCategoryId = searchParams.get("subCategoryId");

      // Also set category/subcategory from URL if present (for compatibility forms)
      if (urlCategoryId && urlCategoryId.trim()) {
        setSelectedCategoryId(urlCategoryId);
      }
      if (urlSubCategoryId && urlSubCategoryId.trim()) {
        setSelectedSubCategoryId(urlSubCategoryId);
      }

      if (chatId && chatId.trim()) {
        setHasCheckedUrlChat(true);

        if (isAuthenticated) {
          const foundChat = chats.find((c) => c.id === chatId);
          try {
            await openChat(chatId);
          } catch (error) {
            console.error("Failed to load chat from URL:", error);
            toast.error(t("chatInterface.failedToLoadChat"));
          }
        }
      } else {
        setHasCheckedUrlChat(true);
      }
    };

    checkUrlForChat();
  }, [
    searchParams,
    chats,
    isAuthenticated,
    openChat,
    hasCheckedUrlChat,
    currentChat,
  ]);

  useEffect(() => {
    hasCheckedUserBirthDetails.current = false;
  }, [user?._id, isAuthenticated]);

  useEffect(() => {
    const loadTodayQuestion = async () => {
      try {
        const data = await getHeadlineByDate(new Date());
        if (data?.dailyQuestion) {
          const lang = localStorage.getItem("preferredLanguage");
          if (lang === "th") {
            setTodayHeadline(data.dailyQuestion_in_thai);
          } else if (lang === "ar") {
            setTodayHeadline(data.dailyQuestion_in_arabic);
          } else {
            setTodayHeadline(data.dailyQuestion);
          }

          if (
            data.lucky_color ||
            data.energy_level ||
            data.golden_hour ||
            data.color_code
          ) {
            setAstrologyData({
              luckyColor: data.lucky_color,
              energyLevel: data.energy_level,
              goldenHour: data.golden_hour,
              color_code: data.color_code,
            });
          }
        }
      } catch (e) {
        console.error("Failed to load dailyQuestion:", e);
      }
    };

    loadTodayQuestion();
  }, [getHeadlineByDate]);

  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const savedChats = localStorage.getItem(LOCAL_STORAGE_KEY);
        const savedPrompt = localStorage.getItem(DEFAULT_PROMPT_KEY);

        if (savedPrompt) setDefaultPrompt(savedPrompt);

        if (savedChats) {
          const parsed: LocalStorageChats = JSON.parse(savedChats);

          parsed.chats = (parsed.chats || []).map((chat: any) => ({
            ...chat,
            createdAt: new Date(chat.createdAt),
            updatedAt: new Date(chat.updatedAt),
            messages: (chat.messages || []).map((msg: any) => ({
              ...msg,
              timestamp: new Date(msg.timestamp),
            })),
          }));

          parsed.chats = parsed.chats.filter(
            (c) => (c.messages?.length || 0) > 0,
          );

          if (parsed.chats.length > 0) {
            const stillExists = parsed.chats.some(
              (c) => c.id === parsed.currentChatId,
            );
            parsed.currentChatId = stillExists
              ? parsed.currentChatId
              : parsed.chats[0].id;

            setAnonymousChats(parsed);
          } else {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            setAnonymousChats({ chats: [], currentChatId: null });
          }
        } else {
          setAnonymousChats({ chats: [], currentChatId: null });
        }

        if (!isAuthenticated) {
          setIsAnonymousMode(true);
        }
      } catch (error) {
        console.error("Failed to load from localStorage:", error);
        setAnonymousChats({ chats: [], currentChatId: null });
        if (!isAuthenticated) setIsAnonymousMode(true);
      }
    };

    loadFromStorage();
  }, [isAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    const checkUserBirthDetails = async () => {
      if (hasCheckedUserBirthDetails.current || !isMounted) {
        return;
      }

      if (!isAuthenticated || !user?._id) {
        return;
      }

      const userBirthDetails = localStorage.getItem("userBirthDetails");

      if (userBirthDetails) {
        if (isMounted) {
          hasCheckedUserBirthDetails.current = true;
        }
        return;
      }

      try {
        const response = await api.get(`/backend/user/${user._id}`);
        const userData = response.data;

        if (!isMounted) return;

        if (!userData.dob) {
          setIsBirthDetailsPopupOpen(true);
        } else {
          const birthDetails = {
            dob: userData.dob,
            birthTime: userData.dob_time || "",
            birthPlace: userData.dob_place || "",
          };
          localStorage.setItem(
            "userBirthDetails",
            JSON.stringify(birthDetails),
          );
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        if (isMounted) {
          hasCheckedUserBirthDetails.current = true;
        }
      }
    };

    checkUserBirthDetails();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user?._id]);

  const activeCategories = useMemo(() => {
    return categories
      .filter((category) => category.isActive)
      .map((category) => {
        const subcats = subCategories.filter(
          (sc) => sc.categoryId === category.id && sc.isActive,
        );
        return {
          ...category,
          subCategoryCount: subcats.length,
          subCategories: subcats,
        };
      });
  }, [categories, subCategories]);

  const isAstriaSpanish = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    return category?.name === "Astria Spanish";
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  // "Astria Spanish V2" — same 7-tone engine as Astria Spanish, but replies
  // render as the premium multi-country card (SpanishV2MessageCard) per the
  // client's "Spanish Lane v4" spec. See spanishLaneV4Config.ts.
  const isAstriaSpanishV2 = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    return category?.name === "Astria Spanish V2";
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  const healjai = useMemo(() => {
    return categories
      .filter(
        (category) =>
          category.name === "HealJai Talk" || category.name === "Astria Talk",
      )
      .map((category) => {
        const subcats = subCategories.filter(
          (sc) => sc.categoryId === category.id && sc.isActive,
        );
        return {
          ...category,
          subCategoryCount: subcats.length,
          subCategories: subcats,
        };
      });
  }, [categories, subCategories]);

  const handleCategorySelect = async (categoryId: string) => {
    setSelectedCategoryId(categoryId);

    const category = activeCategories.find((c) => c.id === categoryId);

    if (category?.subCategoryCount > 0) {
      try {
        setIsCreatingChat(true);
        const subcats = await getSubCategoriesByCategory(categoryId);
        const activeSubcats = subcats.filter((sc: any) => sc.isActive);

        if (activeSubcats.length > 0) {
          setSelectedCategoryForSub(category);
          setAvailableSubcategories(
            activeSubcats.map((sc: any) => ({
              id: sc.id,
              name: sc.name,
              name_th: sc.name_th,
              name_es: sc.name_es,
              name_id: sc.name_id,
              name_ar: sc.name_ar,
              description: sc.description,
              description_th: sc.description_th,
              description_es: sc.description_es,
              description_id: sc.description_id,
              description_ar: sc.description_ar,
              icon: sc.icon,
              color: sc.color,
            })),
          );
          setShowSubcategoryModal(true);
        } else {
          const newChat = await startNewChat(categoryId);
          if (newChat?.id) {
            navigate(`/sanctuary?chat=${newChat.id}`);
          }
        }
      } catch (error) {
        console.error("Failed to load subcategories:", error);
        toast.error(t("chatInterface.failedToCreateChat"));
      } finally {
        setIsCreatingChat(false);
      }
    } else {
      try {
        setIsCreatingChat(true);
        const newChat = await startNewChat(categoryId);
        if (newChat?.id) {
          navigate(`/sanctuary?chat=${newChat.id}`);
        }
      } catch (error) {
        console.error("Failed to start new chat:", error);
        toast.error(t("chatInterface.failedToCreateChat"));
      } finally {
        setIsCreatingChat(false);
      }
    }
  };

  const handleSubcategorySelect = async (subCategoryId: string) => {
    setSelectedSubCategoryId(subCategoryId);
    setShowSubcategoryModal(false);

    try {
      setIsCreatingChat(true);
      const newChat = await startNewChat(
        selectedCategoryForSub?.id || selectedCategoryId || undefined,
        subCategoryId,
      );

      if (newChat) {
        if (newChat?.id) {
          navigate(`/sanctuary?chat=${newChat.id}`);
        }
      }
    } catch (error) {
      console.error("Failed to create chat with subcategory:", error);
      toast.error(t("chatInterface.failedToCreateChat"));
    } finally {
      setIsCreatingChat(false);
    }
  };

  const handleSend = async () => {
    if (
      !input.trim() ||
      (isAuthenticated && isLoading) ||
      (!isAuthenticated && isAnonymousLoading)
    ) {
      return;
    }

    const message = input.trim();
    const detected = extractBirthData(message);
    if (detected && !hasBirthData()) {
      saveBirthData(detected);
    }

    setInput("");

    try {
      if (isAuthenticated) {
        let chatId = currentChat?.id;

        const effectiveCategoryId =
          selectedCategoryId || currentChat?.categoryId || null;
        const effectiveSubCategoryId =
          selectedSubCategoryId || currentChat?.subCategoryId || null;

        if (
          !chatId ||
          (selectedSubCategoryId &&
            currentChat?.subCategoryId !== selectedSubCategoryId)
        ) {
          const newChat = await startNewChat(
            effectiveCategoryId,
            effectiveSubCategoryId,
          );
          chatId = newChat?.id || newChat?._id;

          if (!chatId) {
            toast.error(t("chatInterface.unableToCreateChat"));
            return;
          }
        }

        if (!chatId) {
          toast.error(t("chatInterface.unableToCreateChat"));
          return;
        }

        if (sendMessageStream) {
          setIsStreamingActive(true);
          const jp3BoxSelf = pendingJapan3BoxSelf;
          const jp3BoxPartner = pendingJapan3BoxPartner;
          setPendingJapan3BoxSelf(null);
          setPendingJapan3BoxPartner(null);
          // Indonesia payloads persist for every message (not cleared)
          const indoSelf = isAstriaIndonesiaCompatibility
            ? indonesiaCompatSelf
            : indonesia3BoxPayload;
          const indoPartner = isAstriaIndonesiaCompatibility
            ? indonesiaCompatPartner
            : null;
          try {
            await sendMessageStream(
              message,
              chatId,
              effectiveCategoryId,
              effectiveSubCategoryId,
              isAstriaSpanish || isAstriaSpanishV2 ? spanishTone : null,
              jp3BoxSelf,
              jp3BoxPartner,
              null,
              null,
              null,
              null,
              indoSelf,
              indoPartner,
              isAstriaKoreaHybrid ? koreaHybridMode : null,
              isAstriaJapanHybrid ? japanHybridMode : null,
            );
          } finally {
            setIsStreamingActive(false);
          }
        }

        setSelectedCategoryId(null);
        setSelectedSubCategoryId(null);
        setShowTextArea(false);

        return;
      }
    } catch (error: any) {
      console.error("Send error:", error);
      toast.error(
        error?.response?.data?.message ||
          t("chatInterface.failedToSendMessage"),
      );
    } finally {
      if (!isAuthenticated) {
        setIsAnonymousLoading(false);
        setIsProcessingResponse(false);
      }

      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  // Japan 3-Box direct submit — bypasses input state to send 3-box data atomically
  const handleJapan3BoxSubmit = async (
    message: string,
    selfData: Japan3BoxData,
    partnerData: Japan3BoxData,
  ) => {
    if (!isAuthenticated || isLoading) {
      return;
    }
    setInput("");
    try {
      let chatId = currentChat?.id;
      const effectiveCategoryId =
        selectedCategoryId || currentChat?.categoryId || null;
      const effectiveSubCategoryId =
        selectedSubCategoryId || currentChat?.subCategoryId || null;

      if (
        !chatId ||
        (selectedSubCategoryId &&
          currentChat?.subCategoryId !== selectedSubCategoryId)
      ) {
        const newChat = await startNewChat(
          effectiveCategoryId,
          effectiveSubCategoryId,
        );
        chatId = newChat?.id || newChat?._id;
        if (!chatId) {
          toast.error(t("chatInterface.unableToCreateChat"));
          return;
        }
      }
      if (!chatId) {
        toast.error(t("chatInterface.unableToCreateChat"));
        return;
      }

      if (sendMessageStream) {
        setIsStreamingActive(true);
        try {
          await sendMessageStream(
            message,
            chatId,
            effectiveCategoryId,
            effectiveSubCategoryId,
            null,
            selfData,
            partnerData,
          );
        } finally {
          setIsStreamingActive(false);
        }
      }
      // Hide form, save submitted data, show result
      setSubmittedJapanSelf(selfData);
      setSubmittedJapanPartner(partnerData);
      setShowJapanCompatForm(false);
      setShowJapanResult(true);
      setSelectedCategoryId(null);
      setSelectedSubCategoryId(null);
      setShowTextArea(false);
    } catch (error: any) {
      console.error("Japan 3-Box send error:", error);
      toast.error(
        error?.response?.data?.message ||
          t("chatInterface.failedToSendMessage"),
      );
    }
  };

  // ── Korea 3-Box Submit Handler ──
  const handleKorea3BoxSubmit = async (
    message: string,
    selfData: Korea3BoxData,
    partnerData: Korea3BoxData,
  ) => {
    if (!isAuthenticated || isLoading) {
      return;
    }
    setInput("");
    try {
      let chatId = currentChat?.id;
      const effectiveCategoryId =
        selectedCategoryId || currentChat?.categoryId || null;
      const effectiveSubCategoryId =
        selectedSubCategoryId || currentChat?.subCategoryId || null;

      if (
        !chatId ||
        (selectedSubCategoryId &&
          currentChat?.subCategoryId !== selectedSubCategoryId)
      ) {
        const newChat = await startNewChat(
          effectiveCategoryId,
          effectiveSubCategoryId,
        );
        chatId = newChat?.id || newChat?._id;
        if (!chatId) {
          toast.error(t("chatInterface.unableToCreateChat"));
          return;
        }
      }
      if (!chatId) {
        toast.error(t("chatInterface.unableToCreateChat"));
        return;
      }

      // Store pending Korea 3-box data
      setPendingKorea3BoxSelf(selfData);
      setPendingKorea3BoxPartner(partnerData);

      if (sendMessageStream) {
        setIsStreamingActive(true);
        try {
          await sendMessageStream(
            message,
            chatId,
            effectiveCategoryId,
            effectiveSubCategoryId,
            null,
            null, // japan3BoxSelf
            null, // japan3BoxPartner
            selfData, // korea3BoxSelf
            partnerData, // korea3BoxPartner
          );
        } finally {
          setIsStreamingActive(false);
        }
      }
      // Hide compatibility form and show chat messages
      setShowKoreaCompatForm(false);
      setSelectedCategoryId(null);
      setSelectedSubCategoryId(null);
      setShowTextArea(false);
    } catch (error: any) {
      console.error("Korea 3-Box send error:", error);
      toast.error(
        error?.response?.data?.message ||
          t("chatInterface.failedToSendMessage"),
      );
    }
  };

  // ── Indonesia 3-Box Submit Handler (single-user, all non-compatibility tabs) ──
  const handleIndonesia3BoxSubmit = async (data: Indonesia3BoxData) => {
    if (!isAuthenticated || isLoading) return;
    setInput("");

    // Store payload — will be re-sent on every subsequent message in this session
    setIndonesia3BoxPayload(data);
    setShowIndonesia3Box(false); // hide form, reveal chat

    try {
      let chatId = currentChat?.id;
      const effectiveCategoryId =
        selectedCategoryId || currentChat?.categoryId || null;
      const effectiveSubCategoryId =
        selectedSubCategoryId || currentChat?.subCategoryId || null;

      if (
        !chatId ||
        (selectedSubCategoryId &&
          currentChat?.subCategoryId !== selectedSubCategoryId)
      ) {
        const newChat = await startNewChat(
          effectiveCategoryId,
          effectiveSubCategoryId,
        );
        chatId = newChat?.id || newChat?._id;
        if (!chatId) {
          toast.error(t("chatInterface.unableToCreateChat"));
          return;
        }
      }
      if (!chatId) {
        toast.error(t("chatInterface.unableToCreateChat"));
        return;
      }

      const autoMessage =
        "Tolong analisis profil emosional saya berdasarkan data 3-box saya.";
      if (sendMessageStream) {
        setIsStreamingActive(true);
        try {
          await sendMessageStream(
            autoMessage,
            chatId,
            effectiveCategoryId,
            effectiveSubCategoryId,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            data,
            null,
          );
        } finally {
          setIsStreamingActive(false);
        }
      }
      setSelectedCategoryId(null);
      setSelectedSubCategoryId(null);
      setShowTextArea(false);
    } catch (error: any) {
      console.error("Indonesia 3-Box send error:", error);
      toast.error(
        error?.response?.data?.message ||
          t("chatInterface.failedToSendMessage"),
      );
    }
  };

  // ── Indonesia Compatibility Submit Handler (two-person) ──
  const handleIndonesiaCompatSubmit = async (
    selfData: Indonesia3BoxData,
    partnerData: Indonesia3BoxData,
  ) => {
    if (!isAuthenticated || isLoading) return;
    setInput("");

    // Store both profiles — re-sent on every subsequent message in this session
    setIndonesiaCompatSelf(selfData);
    setIndonesiaCompatPartner(partnerData);
    setShowIndonesiaCompatForm(false); // hide form, reveal chat

    try {
      let chatId = currentChat?.id;
      const effectiveCategoryId =
        selectedCategoryId || currentChat?.categoryId || null;
      const effectiveSubCategoryId =
        selectedSubCategoryId || currentChat?.subCategoryId || null;

      if (
        !chatId ||
        (selectedSubCategoryId &&
          currentChat?.subCategoryId !== selectedSubCategoryId)
      ) {
        const newChat = await startNewChat(
          effectiveCategoryId,
          effectiveSubCategoryId,
        );
        chatId = newChat?.id || newChat?._id;
        if (!chatId) {
          toast.error(t("chatInterface.unableToCreateChat"));
          return;
        }
      }
      if (!chatId) {
        toast.error(t("chatInterface.unableToCreateChat"));
        return;
      }

      const selfSummary = `Calm Type: ${selfData.inner_calm_type}, DOB: ${selfData.dob}, Moment: ${selfData.moment_state}`;
      const partnerSummary = `Calm Type: ${partnerData.inner_calm_type}, DOB: ${partnerData.dob}, Moment: ${partnerData.moment_state}`;
      const autoMessage = `Tolong analisis kecocokan emosional kami berdua.\nPerson A (Saya): ${selfSummary}\nPerson B (Pasangan): ${partnerSummary}`;

      if (sendMessageStream) {
        setIsStreamingActive(true);
        try {
          await sendMessageStream(
            autoMessage,
            chatId,
            effectiveCategoryId,
            effectiveSubCategoryId,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            selfData,
            partnerData,
          );
        } finally {
          setIsStreamingActive(false);
        }
      }
      setSelectedCategoryId(null);
      setSelectedSubCategoryId(null);
      setShowTextArea(false);
    } catch (error: any) {
      console.error("Indonesia Compatibility send error:", error);
      toast.error(
        error?.response?.data?.message ||
          t("chatInterface.failedToSendMessage"),
      );
    }
  };

  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const getCategoryById = (id: string) => categories.find((c) => c.id === id);
  const getSubCategoryById = (id: string) =>
    subCategories.find((s) => s.id === id);

  const urlChatId = searchParams.get("chat");
  const hasChatInUrl = !!urlChatId;

  const isEmptyCurrentChat =
    isAuthenticated &&
    !hasChatInUrl &&
    (!currentChat ||
      !currentChat?.messages ||
      currentChat.messages.length === 0);

  const effectiveCurrentChat = isEmptyCurrentChat ? null : currentChat;

  const getCurrentChat = () => {
    if (isAuthenticated) return effectiveCurrentChat;
    if (isAnonymousMode && anonymousChats.currentChatId) {
      return anonymousChats.chats.find(
        (c) => c.id === anonymousChats.currentChatId,
      );
    }
    return null;
  };

  const currentDisplayChat = getCurrentChat();

  const displayLoading = isAuthenticated ? isLoading : isAnonymousLoading;
  const displayTyping = isAuthenticated ? isTyping : isProcessingResponse;
  const shouldShowTypingIndicator =
    displayTyping &&
    (!currentDisplayChat?.messages?.length ||
      currentDisplayChat.messages[currentDisplayChat.messages.length - 1]
        ?.sender === "user");

  useEffect(() => {
    const messages = currentDisplayChat?.messages || [];
    if (!messages.length) return;

    let lastAiIndex = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.sender === "ai") {
        lastAiIndex = i;
        break;
      }
    }

    if (lastAiIndex === -1) return;

    const lastAi = messages[lastAiIndex];
    const lastAiKey = getMessageKey(lastAi, lastAiIndex);

    if (lastAiKey !== lastAiMessageIdRef.current) {
      lastAiMessageIdRef.current = lastAiKey;
      setHighlightedAiMessageId(lastAiKey);

      const timeout = window.setTimeout(() => {
        setHighlightedAiMessageId(null);
      }, 3000);

      return () => window.clearTimeout(timeout);
    }
  }, [currentDisplayChat?.messages]);

  const handleDeleteClick = () => setDeleteModalOpen(true);
  const handleDeleteCancel = () => setDeleteModalOpen(false);

  const handleDeleteConfirm = async () => {
    if (isAuthenticated && currentChat) {
      const success = await deleteChat(currentChat.id);
      if (success) {
        setDeleteModalOpen(false);
        await fetchChats();
        setWelcomeMessages((prev) => {
          const next = { ...prev };
          delete next[currentChat.id];
          return next;
        });

        const url = new URL(window.location.href);
        url.searchParams.delete("chat");
        window.history.replaceState({}, "", url.toString());
      }
    } else if (!isAuthenticated && anonymousChats.currentChatId) {
      setAnonymousChats((prev) => ({
        chats: prev.chats.filter(
          (chat) => chat.id !== anonymousChats.currentChatId,
        ),
        currentChatId: prev.chats.length > 1 ? prev.chats[0].id : null,
      }));
      setDeleteModalOpen(false);
      toast.success(t("chatInterface.chatDeleted"));
      const url = new URL(window.location.href);
      url.searchParams.delete("chat");
      window.history.replaceState({}, "", url.toString());
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("chatInterface.copiedToClipboard"));
  };

  const toggleExpandedMessage = (messageKey: string) => {
    setExpandedMessageIds((prev) => ({
      ...prev,
      [messageKey]: !prev[messageKey],
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBirthDetailsSave = (details: any) => {
    localStorage.setItem("userBirthDetails", JSON.stringify(details));
    setIsBirthDetailsPopupOpen(false);
    toast.success(t("chatInterface.BirthDetailsText"));
  };

  const isHealJaiTalk = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = healjai.find((c) => c.id === currentChat.categoryId);
    return (
      category?.name === "HealJai Talk" || category?.name === "Astria Talk"
    );
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  const isLogoCategory = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    const subCategory = subCategories.find(
      (s) => s.id === currentChat?.subCategoryId,
    );
    return (
      category?.name === "HealJai Talk" ||
      category?.name === "Astria Talk" ||
      category?.name === "Uranian" ||
      subCategory?.name === "ThaiAstro" ||
      subCategory?.name === "Uranian"
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  const isThaiAstro = useMemo(() => {
    if (!isAuthenticated || !currentChat?.subCategoryId) return false;
    let temp = false;
    const subCategory = subCategories.find(
      (s) => s.id === currentChat?.subCategoryId,
    );
    if (subCategory?.name === "ThaiAstro") {
      temp = true;
    }
    if (subCategory?.name === "ThaiAstro V2") {
      temp = true;
    }
    if (subCategory?.name === "ThaiAstro V3") {
      temp = true;
    }
    if (subCategory?.name === "Uranian") {
      temp = true;
    }
    if (subCategory?.name === "Uranian V2") {
      temp = true;
    }
    if (subCategory?.name === "Uranian V3") {
      temp = true;
    }
    if (subCategory?.name === "รหัส Healjai") {
      temp = true;
    }
    if (subCategory?.name === "รหัส Healjai V3") {
      temp = true;
    }
    return temp;
  }, [currentChat?.subCategoryId, subCategories, isAuthenticated]);

  // True when user is in the "Astria Korea Hybrid" category — gates the
  // Hybrid/Traditional response-mode toggle above the message input. Also
  // checks selectedCategoryId (for a newly created chat before currentChat
  // is set), matching the isAstriaJapanCompatibility pattern below.
  const isAstriaKoreaHybrid = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name === "Astria Korea Hybrid";
  }, [
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
    isAuthenticated,
  ]);

  const isAstriaBrazil = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    return category?.name === "Astria Brazil";
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  // True when user is in Astria Japan category AND on the Compatibility subcategory
  // Also checks selectedCategoryId/selectedSubCategoryId for newly created chats (before currentChat is set)
  const isAstriaJapanCompatibility = useMemo(() => {
    if (!isAuthenticated) return false;
    // Check both currentChat (for existing chats) and selectedCategoryId/selectedSubCategoryId (for new chats)
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    if (category?.name !== "Astria Japan") return false;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return !!(
      subCat?.name && subCat.name.toLowerCase().includes("compatibility")
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  // True when user is in Astria Korea category AND on the Compatibility subcategory
  const isAstriaKoreaCompatibility = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    if (category?.name !== "Astria Korea") return false;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return !!(
      subCat?.name && subCat.name.toLowerCase().includes("compatibility")
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  // True for ALL Astria Korea V2 AND Astria Korea V3 subcategories — drives
  // the pastel/K-style "New Tab" card renderer for AI responses, and (for
  // Compatibility) the dedicated 3-box form/result flow. V3 intentionally
  // reuses the exact same UI as V2 (same card, same compatibility form/result
  // components) since its astrology tabs mirror V2's 1:1. Isolated to these
  // two category names only; zero effect on "Astria Korea" (v1) or any other
  // category's chat UI.
  const isAstriaKoreaV2 = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return (
      category?.name === "Astria Korea V2" ||
      category?.name === "Astria Korea V3"
    );
  }, [
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
    isAuthenticated,
  ]);

  // True when the active category is specifically "Astria Korea V3" (as
  // opposed to V2) — V3 always replies in Korean regardless of user language
  // (see KR_V3_LANGUAGE_RULE in AstriaKoreaV3Service.js), so UI copy for V3
  // must always be Korean too, unlike V2 whose reply language follows the
  // user. Used to fix the label language mismatch in
  // KoreaV2CompatibilityResult.tsx (was picking English/Korean labels off
  // the unrelated `region` setting instead of the category actually in use).
  const isAstriaKoreaV3Category = useMemo(() => {
    if (!isAstriaKoreaV2) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name === "Astria Korea V3";
  }, [
    isAstriaKoreaV2,
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
  ]);

  // Current subcategory name for Astria Korea V2 / V3 (Daily Flow KR v2/v3,
  // Life Map KR, Relationship Engine KR, Daily Companion KR, Compatibility
  // KR) — used to pick the right card layout/icons in AstriaKoreaV2Card.
  const astriaKoreaV2SubCategoryName = useMemo(() => {
    if (!isAstriaKoreaV2) return null;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return subCat?.name || null;
  }, [
    isAstriaKoreaV2,
    currentChat?.subCategoryId,
    selectedSubCategoryId,
    subCategories,
  ]);

  // True for the "Astria Japan Hybrid" category — drives the minimal
  // key-driven JP card renderer (AstriaJapanHybridCard) for AI responses.
  // Isolated to this category name only; zero effect on "Astria Japan",
  // "Astria Japan V3", "Astria Japan Talk", or any Korea category's chat UI.
  const isAstriaJapanHybrid = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name === "Astria Japan Hybrid";
  }, [
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
    isAuthenticated,
  ]);

  // Current subcategory name for Astria Japan Hybrid (Daily Flow JP Hybrid,
  // Daily Companion JP Hybrid, Life Map JP Hybrid, Food JP Hybrid,
  // Relationship JP Hybrid, Compatibility JP Hybrid, Energy Match JP Hybrid)
  // — used to pick the right tab layout in AstriaJapanHybridCard.
  const astriaJapanHybridSubCategoryName = useMemo(() => {
    if (!isAstriaJapanHybrid) return null;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return subCat?.name || null;
  }, [
    isAstriaJapanHybrid,
    currentChat?.subCategoryId,
    selectedSubCategoryId,
    subCategories,
  ]);

  // "Astria Japan Talk" card UI is disabled by product decision — this
  // category must always render as plain prose, never the card layout
  // (Korea's card UI is unaffected; see isAstriaKoreaV2 below). Kept as a
  // constant `false` rather than deleting the flag/AstriaJapanTalkCard
  // wiring, so re-enabling later is a one-line revert.
  const isAstriaJapanTalk = false;

  // Current subcategory name for Astria Japan Talk — used to pick the right
  // card layout/tab in AstriaJapanTalkCard. When "Astria Japan Talk" is
  // itself the category (not a subcategory), this falls back to the
  // category name so the card still resolves the companion (daily) mode.
  const astriaJapanTalkSubCategoryName = useMemo(() => {
    if (!isAstriaJapanTalk) return null;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    if (subCat?.name) return subCat.name;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name || null;
  }, [
    isAstriaJapanTalk,
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
  ]);

  // "Astria Indonesia Talk" card UI — the soft/warm "Teman Bicara" card
  // (Companion/Coach/Culture/Primbon Light modes, see
  // helper/AstriaIndonesiaTalkService.js on the backend), styled per the
  // client's Indo Emotional UI Pack v1 (Ui.txt). Unlike isAstriaJapanTalk
  // above, this ships enabled — no product decision to disable it yet.
  // Isolated to category "Astria Indonesia Talk" only; zero effect on
  // "Astria Indonesia" (v1) or "Astria Indonesia V2".
  const isAstriaIndonesiaTalk = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name === "Astria Indonesia Talk";
  }, [
    isAuthenticated,
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
  ]);

  // Current subcategory name for Astria Indonesia Talk — used to pick the
  // right mode (Companion/Coach/Culture/Primbon Light) in
  // AstriaIndonesiaTalkCard. Falls back to the category name so the card
  // still resolves the companion (default) mode when "Astria Indonesia
  // Talk" is itself the category, not a subcategory.
  const astriaIndonesiaTalkSubCategoryName = useMemo(() => {
    if (!isAstriaIndonesiaTalk) return null;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    if (subCat?.name) return subCat.name;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name || null;
  }, [
    isAstriaIndonesiaTalk,
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
  ]);

  // True when user is in Astria Korea V2 category AND on its Compatibility
  // subcategory — drives the dedicated 3-box form + result page flow, same
  // structure as v1's isAstriaKoreaCompatibility but fully isolated to V2 so
  // Astria Korea (v1) Compatibility is never touched.
  const isAstriaKoreaV2Compatibility = useMemo(() => {
    if (!isAstriaKoreaV2) return false;
    if (!astriaKoreaV2SubCategoryName) return false;
    const lower = astriaKoreaV2SubCategoryName.toLowerCase();
    // Matches both the correct spelling and the "Compatability" typo the
    // subcategory was actually created with in the DB.
    return lower.includes("compatibility") || lower.includes("compatability");
  }, [isAstriaKoreaV2, astriaKoreaV2SubCategoryName]);

  // True when user is in Astria GCC category AND on the Compatibility subcategory
  const isAstriaGCCCompatibility = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    if (category?.name !== "Astria GCC") return false;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return !!(
      subCat?.name && subCat.name.toLowerCase().includes("compatibility")
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  // True when user is in the Astria GCC category (any subcategory) — drives
  // the tester-only Gulf/Kuwaiti/Fusha tone preview on the new-chat screen.
  const isAstriaGCC = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    return category?.name === "Astria GCC";
  }, [
    currentChat?.categoryId,
    selectedCategoryId,
    activeCategories,
    isAuthenticated,
  ]);

  // True for ALL Astria Indonesia subcategories (single-user 3-box pre-chat form)
  const isAstriaIndonesia3Box = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    if (category?.name !== "Astria Indonesia") return false;
    // exclude compatibility subcategory — handled separately
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return !(
      subCat?.name && subCat.name.toLowerCase().includes("compatibility")
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  // True only for Astria Indonesia + Compatibility subcategory (two-person form)
  const isAstriaIndonesiaCompatibility = useMemo(() => {
    if (!isAuthenticated) return false;
    const effectiveCategoryId = currentChat?.categoryId || selectedCategoryId;
    const effectiveSubCategoryId =
      currentChat?.subCategoryId || selectedSubCategoryId;
    if (!effectiveCategoryId) return false;
    const category = activeCategories.find((c) => c.id === effectiveCategoryId);
    if (category?.name !== "Astria Indonesia") return false;
    const subCat = subCategories.find((s) => s.id === effectiveSubCategoryId);
    return !!(
      subCat?.name && subCat.name.toLowerCase().includes("compatibility")
    );
  }, [
    currentChat?.categoryId,
    currentChat?.subCategoryId,
    selectedCategoryId,
    selectedSubCategoryId,
    activeCategories,
    subCategories,
    isAuthenticated,
  ]);

  // Reset single-user form + payload when entering a new non-compat Indonesia tab
  useEffect(() => {
    if (isAstriaIndonesia3Box) {
      setShowIndonesia3Box(true);
      setIndonesia3BoxPayload(null);
    }
  }, [isAstriaIndonesia3Box]);

  // Reset compatibility form + profiles when entering Compatibility tab
  useEffect(() => {
    if (isAstriaIndonesiaCompatibility) {
      setShowIndonesiaCompatForm(true);
      setIndonesiaCompatSelf(null);
      setIndonesiaCompatPartner(null);
    }
  }, [isAstriaIndonesiaCompatibility]);

  // Reset Astria Korea V2 Compatibility form + result when entering that tab
  useEffect(() => {
    if (isAstriaKoreaV2Compatibility) {
      setShowKoreaV2CompatForm(true);
      setShowKoreaV2Result(false);
      setPendingKoreaV2Self(null);
      setPendingKoreaV2Partner(null);
    }
  }, [isAstriaKoreaV2Compatibility]);

  // Notify parent (App.tsx) when Japan, Korea, GCC, or Indonesia mode is active so Footer can be hidden
  useEffect(() => {
    if (onJapanCompatActive) {
      onJapanCompatActive(
        isAstriaJapanCompatibility ||
          isAstriaKoreaCompatibility ||
          isAstriaKoreaV2Compatibility ||
          isAstriaGCCCompatibility ||
          isAstriaIndonesia3Box ||
          isAstriaIndonesiaCompatibility,
      );
    }
  }, [
    isAstriaJapanCompatibility,
    isAstriaKoreaCompatibility,
    isAstriaKoreaV2Compatibility,
    isAstriaGCCCompatibility,
    isAstriaIndonesia3Box,
    isAstriaIndonesiaCompatibility,
    onJapanCompatActive,
  ]);

  const astriaAILabel = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return null;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    if (!category?.name) return null;
    if (category.name.startsWith("Astria GCC")) {
      return "مساحتك هادئة وآمنة… وقد تختلف الاستجابات أحياناً.";
    }
    if (
      category.name.startsWith("Astria ") ||
      category.name === "Compatability Talk"
    ) {
      return `${category.name} is AI and can make mistakes.`;
    }
    return null;
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  // True whenever the current chat's category is Astria GCC or Astria GCC V2
  // (unlike isAstriaGCC above, which only matches the exact V1 name and
  // drives the tester-only tone preview).
  const isAstriaGCCBrand = useMemo(() => {
    if (!isAuthenticated || !currentChat?.categoryId) return false;
    const category = activeCategories.find(
      (c) => c.id === currentChat.categoryId,
    );
    return !!category?.name?.startsWith("Astria GCC");
  }, [currentChat?.categoryId, activeCategories, isAuthenticated]);

  const [showGCCPrivacyPopup, setShowGCCPrivacyPopup] = useState(false);

  // Show the GCC privacy popup once per user on their first visit to Astria
  // GCC — builds trust that chats are private before they start talking.
  useEffect(() => {
    if (!isAstriaGCCBrand || !user?._id) return;
    const seenKey = `gccPrivacyPopupSeen_${user._id}`;
    if (!localStorage.getItem(seenKey)) {
      setShowGCCPrivacyPopup(true);
    }
  }, [isAstriaGCCBrand, user?._id]);

  const handleGCCPrivacyPopupConfirm = useCallback(() => {
    if (user?._id) {
      localStorage.setItem(`gccPrivacyPopupSeen_${user._id}`, "true");
    }
    setShowGCCPrivacyPopup(false);
  }, [user?._id]);

  const userName = useMemo(() => {
    if (user?.name) {
      return user.name.split(" ")[0];
    }
    return undefined;
  }, [user]);

  if (!isAuthenticated && !currentDisplayChat) {
    return (
      <AnonymousCopilotWelcome
        input={input}
        setInput={setInput}
        inputRef={inputRef}
        onSend={handleSend}
        disabled={displayLoading}
        headline={headline}
        isElderlyMode={isElderlyMode}
      />
    );
  }

  if (
    isAuthenticated &&
    !currentDisplayChat &&
    hasChatInUrl &&
    !hasCheckedUrlChat
  ) {
    return (
      <div className="flex-1 flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader2
            className="w-8 h-8 animate-spin mx-auto mb-4"
            style={{ color: SIDEBAR_COLORS.amethyst }}
          />
          <p style={{ color: SIDEBAR_COLORS.darkAmethyst }}>
            {t("chatInterface.loadingChat")}
          </p>
        </div>
      </div>
    );
  }

  if (isAuthenticated && !currentDisplayChat && !hasChatInUrl) {
    return (
      <>
        <LoggedInWelcomeWithCategories
          input={input}
          setInput={setInput}
          inputRef={inputRef}
          onSend={handleSend}
          disabled={displayLoading || isCreatingChat}
          headline={headline}
          categories={activeCategories}
          selectedCategoryId={selectedCategoryId}
          onCategorySelect={handleCategorySelect}
          showTextArea={showTextArea}
          onStartWithoutCategory={() => {}}
          isLoading={isCreatingChat}
          setShowTextArea={setShowTextArea}
          setSelectedCategoryId={setSelectedCategoryId}
          isElderlyMode={isElderlyMode}
        />

        <SubcategorySelectionModal
          isOpen={showSubcategoryModal}
          onClose={() => {
            setShowSubcategoryModal(false);
            setSelectedCategoryId(null);
          }}
          categoryName={selectedCategoryForSub?.name || ""}
          subCategories={availableSubcategories}
          onSelect={(subCategoryId: string) =>
            handleSubcategorySelect(subCategoryId)
          }
        />
      </>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col min-h-screen overflow-y-auto"
      style={{
        background: `linear-gradient(180deg, ${weeklyTheme.bgFrom}, ${weeklyTheme.bgTo})`,
        color: weeklyTheme.accent,
      }}
    >
      <style>
        {`
        @keyframes wave-animation {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }

        .wave-emoji {
          display: inline-block;
          animation: wave-animation 2.5s infinite;
          transform-origin: 70% 70%;
          font-size: inherit;
          will-change: transform;
          backface-visibility: hidden;
        }

        @keyframes healjaiLogoPop {
          0% {
            transform: scale(0.8) rotate(-6deg);
            opacity: 0.2;
          }

          20% {
            transform: scale(1.35) rotate(6deg);
            opacity: 1;
          }

          40% {
            transform: scale(1.1) rotate(-3deg);
          }

          60% {
            transform: scale(1.25) rotate(3deg);
          }

          80% {
            transform: scale(1.05) rotate(0deg);
          }

          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        .typewriter-cursor {
          display: inline-block;
          width: 8px;
          height: 1.2em;
          background-color: currentColor;
          margin-left: 2px;
          vertical-align: middle;
          animation: blink 0.8s infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        @keyframes healjaiRingPulse {
          0% {
            transform: scale(0.6);
            opacity: 0;
          }

          20% {
            transform: scale(1.4);
            opacity: 0.35;
          }

          60% {
            transform: scale(1.9);
            opacity: 0.18;
          }

          100% {
            transform: scale(2.2);
            opacity: 0;
          }
        }

        @keyframes healjaiLogoBreathe {
          0% {
            transform: scale(0.98);
            opacity: 0.8;
          }

          50% {
            transform: scale(1.12);
            opacity: 1;
          }

          100% {
            transform: scale(0.98);
            opacity: 0.8;
          }
        }

        @keyframes aiFadeIn {
          from {
            opacity: 0;
            transform: translateY(2px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ai-fade-in {
          animation: aiFadeIn 240ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
          backface-visibility: hidden;
        }

        @keyframes aiTextFade {
          from {
            opacity: 0;
            transform: translateY(1px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ai-text-fade {
          animation: aiTextFade 320ms cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
          backface-visibility: hidden;
        }

        @keyframes loadingPulse {
          0%, 100% {
            opacity: 0.6;
          }

          50% {
            opacity: 1;
          }
        }

        .loading-text-pulse {
          animation: loadingPulse 1.5s ease-in-out infinite;
          will-change: opacity;
        }

        /* Smooth AI streaming animation */
        @keyframes smoothStreamAppear {
          0% {
            opacity: 0;
            transform: translateY(1px);
          }

          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .streaming-message {
          animation: smoothStreamAppear 0.35s cubic-bezier(0.22, 1, 0.36, 1);
          will-change: opacity, transform;
          backface-visibility: hidden;
        }
      `}
      </style>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("chatInterface.deleteChat")}
        message={t("chatInterface.areYouSureDeleteChat")}
        itemName={currentDisplayChat?.title || "แชทนี้"}
      />

      {/* Chat header — hidden on Japan or Korea Compatibility for all sizes */}
      <div
        className="border-b p-4 backdrop-blur-xl"
        style={{
          backgroundColor: `${SIDEBAR_COLORS.pearl}`,
          borderColor: `${SIDEBAR_COLORS.roseGold}40`,
          ...(isAstriaJapanCompatibility ||
          isAstriaKoreaCompatibility ||
          isAstriaKoreaV2Compatibility ||
          isAstriaGCCCompatibility
            ? { display: "none" }
            : {}),
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={() => {
                setSelectedCategoryId(null);
                setSelectedSubCategoryId(null);
                setShowTextArea(false);
              }}
              className={`inline-flex items-center justify-center h-10 w-10 rounded-xl border border-black/10 hover:bg-black/5 transition ${
                isElderlyMode ? "icon-button" : ""
              }`}
              title={t("chatInterface.back")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            {isAstriaGCCBrand ? (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "#E9DFCE" }}
              >
                <Heart className="w-5 h-5" style={{ color: "#8A7357" }} />
              </div>
            ) : isAuthenticated && currentChat?.categoryId ? (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${SIDEBAR_COLORS.roseGold}30` }}
              >
                {(() => {
                  const category = activeCategories.find(
                    (c) => c.id === currentChat.categoryId,
                  );
                  if (category) {
                    const Icon = getCategoryIcon(category.icon);
                    return (
                      <Icon
                        size={16}
                        style={{ color: SIDEBAR_COLORS.amethyst }}
                      />
                    );
                  }
                  return (
                    <Bot
                      className="w-5 h-5"
                      style={{ color: SIDEBAR_COLORS.amethyst }}
                    />
                  );
                })()}
              </div>
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${SIDEBAR_COLORS.roseGold}30` }}
              >
                <Bot
                  className="w-5 h-5"
                  style={{ color: SIDEBAR_COLORS.amethyst }}
                />
              </div>
            )}

            <div>
              <div className="flex items-center gap-2">
                <h1
                  className="font-semibold"
                  style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                >
                  {isAstriaGCCBrand
                    ? "هالة"
                    : currentDisplayChat?.title || t("chatInterface.chat")}
                </h1>
              </div>

              {isAuthenticated ? (
                <p
                  className="text-sm"
                  style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                >
                  {activeCategories.find(
                    (c) => c.id === currentChat?.categoryId,
                  )?.name || t("chatInterface.generalChat")}
                  {currentChat?.subCategoryId
                    ? ` • ${subCategories.find((s) => s.id === currentChat.subCategoryId)?.name}`
                    : ""}
                </p>
              ) : (
                <p
                  className="text-sm"
                  style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                >
                  {t("chatInterface.chatWithAIAssistant")}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isAuthenticated && (
              <button
                onClick={() => (window.location.href = "/login")}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-colors ${
                  isElderlyMode ? "healing-button" : ""
                }`}
                style={{ backgroundColor: SIDEBAR_COLORS.amethyst }}
                disabled={displayLoading}
              >
                <LogIn className="w-4 h-4" />
                {t("chatInterface.login")}
              </button>
            )}

            <button
              onClick={handleDeleteClick}
              className={`p-2 hover:opacity-80 rounded-lg ${
                isElderlyMode ? "icon-button" : ""
              }`}
              style={{ color: "#ef4444" }}
              title="ลบแชท"
              disabled={displayLoading}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Chat messages — hidden when Japan, Korea, GCC Compatibility or Indonesia 3-Box is active */}
      <div
        className="flex-1 pb-10"
        style={{
          display:
            (isAstriaJapanCompatibility &&
              (showJapanCompatForm || showJapanResult)) ||
            isAstriaKoreaCompatibility ||
            isAstriaKoreaV2Compatibility ||
            isAstriaGCCCompatibility ||
            (isAstriaIndonesia3Box &&
              showIndonesia3Box &&
              !indonesia3BoxPayload) ||
            (isAstriaIndonesiaCompatibility &&
              showIndonesiaCompatForm &&
              !indonesiaCompatSelf)
              ? "none"
              : undefined,
        }}
      >
        <div className="max-w-3xl mx-auto p-4">
          {isHealJaiTalk && <HealJaiGreeting userName={userName} />}

          {currentDisplayChat?.messages?.length === 0 && !isHealJaiTalk ? (
            <div className="text-center py-12">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{
                  background: `linear-gradient(135deg, ${SIDEBAR_COLORS.roseGold}30, ${SIDEBAR_COLORS.amethyst}15)`,
                  border: `1px solid ${astrologyData.color_code}40`,
                }}
              >
                <Bot
                  className="w-10 h-10"
                  style={{ color: `${astrologyData.color_code}` }}
                />
              </div>
              <h2
                className="text-2xl font-bold mb-3"
                style={{ color: SIDEBAR_COLORS.darkAmethyst }}
              >
                {isAuthenticated
                  ? todayHeadline
                  : t("chatInterface.readyToChat")}
              </h2>
              {isAstriaGCC && user?.roleId === 3 && (
                <GCCTonePreview
                  toneMode={user?.gccToneMode || DEFAULT_GCC_V2_TONE_MODE}
                />
              )}
              <p
                className="mb-2"
                style={{ color: SIDEBAR_COLORS.darkAmethyst }}
              >
                {isAuthenticated
                  ? t("chatInterface.hiImHereToListen")
                  : t("chatInterface.typeMessageBelowToBegin")}
              </p>
            </div>
          ) : (
            <>
              {currentDisplayChat?.messages?.map((message, index) => {
                const messageKey = getMessageKey(message, index);
                const uniqueKey = `${messageKey}_${index}`;
                const showAiLogo =
                  isLogoCategory && highlightedAiMessageId === messageKey;
                const shouldInjectLogos =
                  isLogoCategory && message.sender === "ai";

                // Parse Samay Pravah markers from content (covers both live + history)
                const {
                  displayText: samayDisplayText,
                  energyGraph: contentGraph,
                } = parseSamayPravahContent(message.content || "");
                const samayGraph = message.samayPravahGraph || contentGraph;
                const resolvedContent = samayGraph
                  ? samayDisplayText
                  : message.content || "";
                const markdownContentRaw = shouldInjectLogos
                  ? injectHealJaiLogos(resolvedContent)
                  : resolvedContent;
                const isLongMessage =
                  isThaiAstro &&
                  message.sender === "ai" &&
                  typeof markdownContentRaw === "string" &&
                  markdownContentRaw.length > 500;
                const isExpanded = !!expandedMessageIds[messageKey];
                const markdownContent =
                  isLongMessage && !isExpanded
                    ? `${markdownContentRaw.slice(0, 1500)}...`
                    : markdownContentRaw;

                const isLastMessage =
                  index === (currentDisplayChat?.messages?.length || 0) - 1;
                const isMessageStreaming =
                  isLastMessage && message.sender === "ai" && isStreamingActive;

                // Astria Korea V2 — pastel/K-style card renderer for AI
                // responses, isolated to category "Astria Korea V2" only.
                const isKoreaV2AiMessage =
                  isAstriaKoreaV2 && message.sender === "ai";

                // Astria Japan Talk — minimal/pastel JP card renderer for AI
                // responses, isolated to category "Astria Japan Talk" only.
                const isJapanTalkAiMessage =
                  isAstriaJapanTalk && message.sender === "ai";

                // Astria Japan Hybrid — minimal key-driven JP card renderer
                // for AI responses, isolated to category "Astria Japan
                // Hybrid" only.
                const isJapanHybridAiMessage =
                  isAstriaJapanHybrid && message.sender === "ai";

                // Astria Indonesia Talk — soft/warm "Teman Bicara" card
                // renderer for AI responses, isolated to category
                // "Astria Indonesia Talk" only.
                const isIndonesiaTalkAiMessage =
                  isAstriaIndonesiaTalk && message.sender === "ai";

                // Astria Spanish V2 — premium multi-country accent card
                // renderer for AI responses, isolated to category
                // "Astria Spanish V2" only. See SpanishV2MessageCard.tsx.
                const isSpanishV2AiMessage =
                  isAstriaSpanishV2 && message.sender === "ai";

                return (
                  <div
                    key={uniqueKey}
                    className={`mb-6 ${message.sender === "user" ? "text-right" : "text-left"}`}
                  >
                    <div className="flex items-start gap-3">
                      {message.sender === "ai" && (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 relative"
                          style={{
                            backgroundColor: SIDEBAR_COLORS.amethyst,
                          }}
                        >
                          {showAiLogo && (
                            <span
                              className="absolute inset-0 rounded-full"
                              style={{
                                boxShadow:
                                  "0 0 0 2px rgba(229, 184, 173, 0.65), 0 0 18px rgba(229, 184, 173, 0.55)",
                                animation: "healjaiRingPulse 3s ease-in-out",
                              }}
                            />
                          )}
                          {showAiLogo ? (
                            <img
                              src="/logo_2.png"
                              alt={brand.name}
                              className="w-5 h-5 object-contain"
                              style={{
                                filter:
                                  "drop-shadow(0 0 14px rgba(229, 184, 173, 0.75))",
                                animation: "healjaiLogoPop 3s ease-in-out",
                              }}
                            />
                          ) : (
                            <Bot className="w-4 h-4 text-white" />
                          )}
                        </div>
                      )}

                      <div
                        className={`flex-1 ${message.sender === "user" ? "flex justify-end" : ""}`}
                      >
                        <div
                          className={`inline-block rounded-2xl max-w-[100%] ${
                            (samayGraph ||
                              isKoreaV2AiMessage ||
                              isJapanTalkAiMessage ||
                              isJapanHybridAiMessage ||
                              isIndonesiaTalkAiMessage ||
                              isSpanishV2AiMessage) &&
                            message.sender === "ai"
                              ? ""
                              : "px-4 py-3"
                          } ${
                            message.sender === "user"
                              ? "text-white user-message"
                              : samayGraph ||
                                  isKoreaV2AiMessage ||
                                  isJapanTalkAiMessage ||
                                  isJapanHybridAiMessage ||
                                  isIndonesiaTalkAiMessage ||
                                  isSpanishV2AiMessage
                                ? ""
                                : "border text-gray-900 ai-message"
                          } ${isElderlyMode ? "message-bubble" : ""} ${
                            message.sender === "ai" &&
                            highlightedAiMessageId === messageKey
                              ? "ai-fade-in"
                              : ""
                          }`}
                          style={
                            (samayGraph ||
                              isKoreaV2AiMessage ||
                              isJapanTalkAiMessage ||
                              isJapanHybridAiMessage ||
                              isIndonesiaTalkAiMessage ||
                              isSpanishV2AiMessage) &&
                            message.sender === "ai"
                              ? {}
                              : message.sender === "user"
                                ? {
                                    backgroundColor: SIDEBAR_COLORS.amethyst,
                                  }
                                : {
                                    backgroundColor: "white",
                                    borderColor: `${SIDEBAR_COLORS.roseGold}40`,
                                  }
                          }
                        >
                          {isKoreaV2AiMessage ? (
                            <AstriaKoreaV2Card
                              content={markdownContent}
                              subCategoryName={astriaKoreaV2SubCategoryName}
                              isStreaming={isMessageStreaming}
                              structuredData={message.astriaKoreaV2Data}
                              timestamp={message.timestamp}
                              onCopy={() => copyToClipboard(message.content)}
                              onSelectFollowUp={(q) => {
                                setInput(q);
                                setTimeout(() => inputRef.current?.focus(), 0);
                              }}
                            />
                          ) : null}
                          {isJapanTalkAiMessage ? (
                            <AstriaJapanTalkCard
                              content={markdownContent}
                              subCategoryName={astriaJapanTalkSubCategoryName}
                              isStreaming={isMessageStreaming}
                            />
                          ) : null}
                          {isIndonesiaTalkAiMessage ? (
                            <AstriaIndonesiaTalkCard
                              content={markdownContent}
                              subCategoryName={
                                astriaIndonesiaTalkSubCategoryName
                              }
                              isStreaming={isMessageStreaming}
                              timestamp={message.timestamp}
                              onCopy={() => copyToClipboard(message.content)}
                            />
                          ) : null}
                          {isSpanishV2AiMessage ? (
                            <SpanishV2MessageCard
                              content={markdownContent}
                              spanishTone={spanishTone}
                              timestamp={message.timestamp}
                              onCopy={() => copyToClipboard(message.content)}
                            />
                          ) : null}
                          {!samayGraph &&
                            !isKoreaV2AiMessage &&
                            !isJapanTalkAiMessage &&
                            !isIndonesiaTalkAiMessage &&
                            !isSpanishV2AiMessage && (
                              <div
                                className={`whitespace-pre-wrap ${
                                  message.sender === "ai" &&
                                  highlightedAiMessageId === messageKey
                                    ? "ai-text-fade"
                                    : ""
                                } ${isElderlyMode ? "message-content" : ""}`}
                              >
                                {message.sender === "ai" ? (
                                  <TypewriterText
                                    content={markdownContent}
                                    isStreaming={isMessageStreaming}
                                    components={{
                                      p: ({ node, ...props }) => (
                                        <p className="mb-2" {...props} />
                                      ),
                                      h1: ({ node, ...props }) => (
                                        <h1
                                          className="text-xl font-semibold mt-4 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h2: ({ node, ...props }) => (
                                        <h2
                                          className="text-lg font-semibold mt-4 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h3: ({ node, ...props }) => (
                                        <h3
                                          className="text-base font-semibold mt-3 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h4: ({ node, ...props }) => (
                                        <h4
                                          className="text-sm font-semibold mt-3 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h5: ({ node, ...props }) => (
                                        <h5
                                          className="text-sm font-semibold mt-2 mb-1"
                                          {...props}
                                        />
                                      ),
                                      h6: ({ node, ...props }) => (
                                        <h6
                                          className="text-xs font-semibold mt-2 mb-1"
                                          {...props}
                                        />
                                      ),
                                      ul: ({ node, ...props }) => (
                                        <ul
                                          className="list-disc pl-6 my-2 whitespace-normal"
                                          {...props}
                                        />
                                      ),
                                      ol: ({ node, ...props }) => (
                                        <ol
                                          className="list-decimal pl-6 my-2 whitespace-normal"
                                          {...props}
                                        />
                                      ),
                                      li: ({ node, ...props }) => (
                                        <li
                                          className="my-1 leading-relaxed whitespace-normal [&>p]:m-0 [&>p]:inline"
                                          {...props}
                                        />
                                      ),

                                      table: ({ node, ...props }) => (
                                        <div className="overflow-x-auto my-4">
                                          <table className="w-full border-separate border-spacing-y-2 text-sm">
                                            {props.children}
                                          </table>
                                        </div>
                                      ),

                                      thead: ({ node, ...props }) => (
                                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                          {props.children}
                                        </thead>
                                      ),

                                      tr: ({ node, ...props }) => (
                                        <tr className="bg-white shadow-sm rounded-xl">
                                          {props.children}
                                        </tr>
                                      ),

                                      th: ({ node, ...props }) => (
                                        <th className="text-left px-4 py-3 font-semibold">
                                          {props.children}
                                        </th>
                                      ),

                                      td: ({ node, ...props }) => (
                                        <td className="px-4 py-3 align-middle text-gray-700">
                                          {props.children}
                                        </td>
                                      ),

                                      img: ({ node, ...props }) => {
                                        const isHealJaiLogo =
                                          props.src === "/logo_2.png" ||
                                          props.src === HEALJAI_LOGO ||
                                          props.src === ASTRIA_LOGO;

                                        return (
                                          <img
                                            {...props}
                                            className={
                                              isHealJaiLogo
                                                ? "w-5 h-5 inline ml-1 align-text-bottom"
                                                : "max-w-full h-auto mx-auto my-2"
                                            }
                                          />
                                        );
                                      },
                                    }}
                                  />
                                ) : (
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                      p: ({ node, ...props }) => (
                                        <p className="mb-2" {...props} />
                                      ),
                                      h1: ({ node, ...props }) => (
                                        <h1
                                          className="text-xl font-semibold mt-4 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h2: ({ node, ...props }) => (
                                        <h2
                                          className="text-lg font-semibold mt-4 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h3: ({ node, ...props }) => (
                                        <h3
                                          className="text-base font-semibold mt-3 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h4: ({ node, ...props }) => (
                                        <h4
                                          className="text-sm font-semibold mt-3 mb-2"
                                          {...props}
                                        />
                                      ),
                                      h5: ({ node, ...props }) => (
                                        <h5
                                          className="text-sm font-semibold mt-2 mb-1"
                                          {...props}
                                        />
                                      ),
                                      h6: ({ node, ...props }) => (
                                        <h6
                                          className="text-xs font-semibold mt-2 mb-1"
                                          {...props}
                                        />
                                      ),
                                      ul: ({ node, ...props }) => (
                                        <ul
                                          className="list-disc pl-6 my-2 whitespace-normal"
                                          {...props}
                                        />
                                      ),
                                      ol: ({ node, ...props }) => (
                                        <ol
                                          className="list-decimal pl-6 my-2 whitespace-normal"
                                          {...props}
                                        />
                                      ),
                                      li: ({ node, ...props }) => (
                                        <li
                                          className="my-1 leading-relaxed whitespace-normal [&>p]:m-0 [&>p]:inline"
                                          {...props}
                                        />
                                      ),

                                      table: ({ node, ...props }) => (
                                        <div className="overflow-x-auto my-4">
                                          <table className="w-full border-separate border-spacing-y-2 text-sm">
                                            {props.children}
                                          </table>
                                        </div>
                                      ),

                                      thead: ({ node, ...props }) => (
                                        <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                          {props.children}
                                        </thead>
                                      ),

                                      tr: ({ node, ...props }) => (
                                        <tr className="bg-white shadow-sm rounded-xl">
                                          {props.children}
                                        </tr>
                                      ),

                                      th: ({ node, ...props }) => (
                                        <th className="text-left px-4 py-3 font-semibold">
                                          {props.children}
                                        </th>
                                      ),

                                      td: ({ node, ...props }) => (
                                        <td className="px-4 py-3 align-middle text-gray-700">
                                          {props.children}
                                        </td>
                                      ),

                                      img: ({ node, ...props }) => {
                                        const isHealJaiLogo =
                                          props.src === "/logo_2.png" ||
                                          props.src === HEALJAI_LOGO ||
                                          props.src === ASTRIA_LOGO;

                                        return (
                                          <img
                                            {...props}
                                            className={
                                              isHealJaiLogo
                                                ? "w-5 h-5 inline ml-1 align-text-bottom"
                                                : "max-w-full h-auto mx-auto my-2"
                                            }
                                          />
                                        );
                                      },
                                    }}
                                  >
                                    {markdownContent}
                                  </ReactMarkdown>
                                )}
                              </div>
                            )}

                          {/* Samay Pravah Energy Graph */}
                          {samayGraph && message.sender === "ai" && (
                            <SamayPravahEnergyGraph
                              graph={samayGraph}
                              guidance={samayDisplayText}
                              timestamp={message.timestamp}
                              onCopy={() => copyToClipboard(message.content)}
                            />
                          )}

                          {isLongMessage && (
                            <button
                              type="button"
                              onClick={() => toggleExpandedMessage(messageKey)}
                              className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm border transition ${
                                message.sender === "user"
                                  ? "text-white bg-white/15 border-white/30 hover:bg-white/25"
                                  : "text-gray-900 bg-gray-50 border-gray-200 hover:bg-gray-100"
                              } ${isElderlyMode ? "healing-button" : ""}`}
                            >
                              <span className="tracking-wide">
                                {isExpanded ? "ย่อ" : "อ่านเพิ่มเติม"}
                              </span>
                              <span className="text-[11px]">
                                {isExpanded ? "▲" : "▼"}
                              </span>
                            </button>
                          )}

                          {/* Hide outer timestamp/copy when Samay Pravah graph, Astria Korea V2, Astria Indonesia Talk, or Astria Spanish V2 card is shown (they render their own, inside the card) */}
                          {!(
                            (samayGraph ||
                              isKoreaV2AiMessage ||
                              isIndonesiaTalkAiMessage ||
                              isSpanishV2AiMessage) &&
                            message.sender === "ai"
                          ) && (
                            <div
                              className={`flex items-center justify-between mt-2 pt-2 ${
                                message.sender === "user"
                                  ? "border-white/30"
                                  : "border-gray-100"
                              }`}
                            >
                              <span
                                className={`text-xs ${
                                  message.sender === "user"
                                    ? "text-white/75"
                                    : "text-gray-500"
                                } ${isElderlyMode ? "timestamp" : ""}`}
                              >
                                {new Date(message.timestamp).toLocaleTimeString(
                                  [],
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )}
                              </span>

                              {message.sender === "user" && (
                                <button
                                  onClick={() =>
                                    copyToClipboard(message.content)
                                  }
                                  className={`p-1 rounded ${
                                    isElderlyMode ? "icon-button" : ""
                                  }`}
                                  style={{ color: SIDEBAR_COLORS.pearl }}
                                  title={t("chatInterface.copy")}
                                  disabled={displayLoading}
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}

                              {message.sender === "ai" && (
                                <button
                                  onClick={() =>
                                    copyToClipboard(message.content)
                                  }
                                  className={`p-1 hover:bg-gray-100 rounded ${
                                    isElderlyMode ? "icon-button" : ""
                                  }`}
                                  style={{ color: SIDEBAR_COLORS.darkAmethyst }}
                                  title={t("chatInterface.copy")}
                                  disabled={displayLoading}
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {message.sender === "user" && (
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: SIDEBAR_COLORS.amethyst,
                          }}
                        >
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Typing indicator with loading message */}
          {shouldShowTypingIndicator && (
            <div className="mb-6 text-left" key="typing-indicator">
              <div className="ml-11 flex justify-start gap-4">
                <div className="relative w-12 h-12 flex items-center justify-center mb-2">
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{
                      boxShadow:
                        "0 0 0 2px rgba(229, 184, 173, 0.5), 0 0 18px rgba(229, 184, 173, 0.45)",
                      animation: "healjaiRingPulse 2.6s ease-in-out infinite",
                    }}
                  />
                  <img
                    src="/logo_2.png"
                    alt={`${brand.name} loading`}
                    className="w-7 h-7 object-contain"
                    style={{
                      animation: "healjaiLogoBreathe 1.8s ease-in-out infinite",
                    }}
                  />
                </div>
                {loadingMessage && (
                  <div className="mt-4">
                    <p
                      className="text-sm font-medium loading-text-pulse"
                      style={{ color: SIDEBAR_COLORS.amethyst }}
                    >
                      {loadingMessage}
                      <span className="inline-block ml-1"></span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Japan Compatibility — Form → Result Router Section */}
      {isAstriaJapanCompatibility ? (
        showJapanResult ? (
          <JapanCompatibilityResult
            compatibilityData={currentChat?.japanCompatibilityData || null}
            data={currentChat?.japanCompatibilityData || null}
            selfData={{
              blood_type: submittedJapanSelf?.blood_type || null,
              dob: submittedJapanSelf?.dob || null,
            }}
            partnerData={{
              blood_type: submittedJapanPartner?.blood_type || null,
              dob: submittedJapanPartner?.dob || null,
            }}
            onRetry={() => {
              setShowJapanResult(false);
              setShowJapanCompatForm(true);
              setSubmittedJapanSelf(null);
              setSubmittedJapanPartner(null);
            }}
            onBack={() => {
              setShowJapanResult(false);
              setShowJapanCompatForm(true);
              setSubmittedJapanSelf(null);
              setSubmittedJapanPartner(null);
            }}
          />
        ) : (
          <JapanCompatPanel
            isLoading={displayLoading}
            onSubmit={(selfData, partnerData) => {
              // Capture layout data immediately before states change to prevent sync blackouts
              if (typeof setSubmittedJapanSelf === "function")
                setSubmittedJapanSelf(selfData);
              if (typeof setSubmittedJapanPartner === "function")
                setSubmittedJapanPartner(partnerData);
              if (typeof setShowJapanResult === "function")
                setShowJapanResult(true);
              if (typeof setShowJapanCompatForm === "function")
                setShowJapanCompatForm(false);

              const selfSummary = [
                selfData.blood_type && `Blood Type: ${selfData.blood_type}`,
                selfData.dob && `DOB: ${selfData.dob}`,
                selfData.destiny_time && `Time: ${selfData.destiny_time}`,
                selfData.birth_city && `City: ${selfData.birth_city}`,
              ]
                .filter(Boolean)
                .join(", ");

              const partnerSummary = [
                partnerData.blood_type &&
                  `Blood Type: ${partnerData.blood_type}`,
                partnerData.dob && `DOB: ${partnerData.dob}`,
                partnerData.destiny_time && `Time: ${partnerData.destiny_time}`,
                partnerData.birth_city && `City: ${partnerData.birth_city}`,
              ]
                .filter(Boolean)
                .join(", ");

              const autoMessage = `相性を読み解いてください。\nPerson A (Me): ${selfSummary}\nPerson B (Partner): ${partnerSummary}`;
              handleJapan3BoxSubmit(autoMessage, selfData, partnerData);
            }}
          />
        )
      ) : null}

      {/* Korea Compatibility — Form or Result */}
      {isAstriaKoreaCompatibility ? (
        showKoreaResult && pendingKorea3BoxSelf && pendingKorea3BoxPartner ? (
          <KoreaCompatibilityResult
            selfData={pendingKorea3BoxSelf}
            partnerData={pendingKorea3BoxPartner}
            onBack={() => {
              setShowKoreaResult(false);
              setShowKoreaCompatForm(true);
              setPendingKorea3BoxSelf(null);
              setPendingKorea3BoxPartner(null);
            }}
          />
        ) : showKoreaCompatForm ? (
          <KoreaCompatPanel
            isLoading={displayLoading}
            onSubmit={(selfData, partnerData) => {
              setPendingKorea3BoxSelf(selfData);
              setPendingKorea3BoxPartner(partnerData);
              setShowKoreaResult(true);
              setShowKoreaCompatForm(false);

              const selfSummary = [
                selfData.name && `Name: ${selfData.name}`,
                selfData.gender && `Gender: ${selfData.gender}`,
                selfData.blood_type && `Blood Type: ${selfData.blood_type}`,
                selfData.dob && `DOB: ${selfData.dob}`,
                selfData.destiny_time && `Time: ${selfData.destiny_time}`,
                selfData.birth_city && `City: ${selfData.birth_city}`,
              ]
                .filter(Boolean)
                .join(", ");

              const partnerSummary = [
                partnerData.name && `Name: ${partnerData.name}`,
                partnerData.gender && `Gender: ${partnerData.gender}`,
                partnerData.blood_type &&
                  `Blood Type: ${partnerData.blood_type}`,
                partnerData.dob && `DOB: ${partnerData.dob}`,
                partnerData.destiny_time && `Time: ${partnerData.destiny_time}`,
                partnerData.birth_city && `City: ${partnerData.birth_city}`,
              ]
                .filter(Boolean)
                .join(", ");

              const autoMessage = `궁합을 읽어주세요.\nPerson A (Me): ${selfSummary}\nPerson B (Partner): ${partnerSummary}`;
              handleKorea3BoxSubmit(autoMessage, selfData, partnerData);
            }}
          />
        ) : null
      ) : null}

      {/* Astria Korea V2 Compatibility — Form or Result (isolated from v1) */}
      {isAstriaKoreaV2Compatibility ? (
        showKoreaV2Result && pendingKoreaV2Self && pendingKoreaV2Partner ? (
          <KoreaV2CompatibilityResult
            selfData={pendingKoreaV2Self}
            partnerData={pendingKoreaV2Partner}
            categoryId={currentChat?.categoryId || selectedCategoryId || ""}
            subCategoryId={
              currentChat?.subCategoryId || selectedSubCategoryId || ""
            }
            forceKorean={isAstriaKoreaV3Category}
            onBack={() => {
              setShowKoreaV2Result(false);
              setShowKoreaV2CompatForm(true);
              setPendingKoreaV2Self(null);
              setPendingKoreaV2Partner(null);
            }}
          />
        ) : showKoreaV2CompatForm ? (
          <KoreaV2CompatPanel
            isLoading={displayLoading}
            onSubmit={(selfData, partnerData) => {
              setPendingKoreaV2Self(selfData);
              setPendingKoreaV2Partner(partnerData);
              setShowKoreaV2Result(true);
              setShowKoreaV2CompatForm(false);
            }}
          />
        ) : null
      ) : null}

      {/* GCC Compatibility Form or Loading or Result */}
      {isAstriaGCCCompatibility ? (
        showGCCCompatForm && !pendingGCC3BoxSelf ? (
          <GCCCompatibility3Box
            onBack={() => setShowGCCCompatForm(false)}
            categoryId={selectedCategoryId || ""}
            subCategoryId={selectedSubCategoryId || ""}
            userId={user?._id || ""}
            onSubmit={(selfData, partnerData) => {
              setPendingGCC3BoxSelf(selfData);
              setPendingGCC3BoxPartner(partnerData);
              setShowGCCCompatForm(false);
            }}
          />
        ) : pendingGCC3BoxSelf && pendingGCC3BoxPartner ? (
          <GCCCompatibilityResult
            selfData={pendingGCC3BoxSelf}
            partnerData={pendingGCC3BoxPartner}
            onBack={() => {
              setPendingGCC3BoxSelf(null);
              setPendingGCC3BoxPartner(null);
              setShowGCCCompatForm(true);
            }}
          />
        ) : null
      ) : null}

      {/* Indonesia 3-Box — single-user form (shown until payload is collected) */}
      {isAstriaIndonesia3Box && showIndonesia3Box && !indonesia3BoxPayload && (
        <Indonesia3Box
          onSubmit={handleIndonesia3BoxSubmit}
          isLoading={isStreamingActive}
          forceLanguage={user?.region === "indonesia" ? "id" : "en"}
        />
      )}

      {/* Indonesia Compatibility — two-person form (shown until both profiles collected) */}
      {isAstriaIndonesiaCompatibility &&
        showIndonesiaCompatForm &&
        !indonesiaCompatSelf && (
          <IndonesiaCompatibility3Box
            onSubmit={handleIndonesiaCompatSubmit}
            isLoading={isStreamingActive}
          />
        )}

      {/* Input area — hidden when Japan, Korea, GCC Compatibility or Indonesia 3-Box is active */}
      <div
        className="border-t p-3 md:p-4 sticky backdrop-blur-xl transition-all duration-300"
        style={{
          bottom: showFooter
            ? "calc(60px + env(safe-area-inset-bottom))"
            : "env(safe-area-inset-bottom)",
          backgroundColor: `${SIDEBAR_COLORS.pearl}`,
          borderColor: `${SIDEBAR_COLORS.roseGold}40`,
          ...(isAstriaJapanCompatibility ||
          isAstriaKoreaCompatibility ||
          isAstriaKoreaV2Compatibility ||
          isAstriaGCCCompatibility ||
          (isAstriaIndonesia3Box && !indonesia3BoxPayload) ||
          (isAstriaIndonesiaCompatibility && !indonesiaCompatSelf)
            ? { display: "none" }
            : {}),
        }}
      >
        {/* Starter-question chips shown before the first Korea V2 message; normal input takes over once chat starts */}
        {isAstriaKoreaV2 &&
          !isAstriaKoreaV2Compatibility &&
          (currentDisplayChat?.messages?.length ?? 0) === 0 && (
            <div className="max-w-3xl mx-auto mb-3">
              <AstriaKoreaV2Suggestions
                subCategoryName={astriaKoreaV2SubCategoryName}
                onSelectPrompt={(text) => {
                  setInput(text);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
              />
            </div>
          )}

        {/* Spanish tone selector — hidden until spanishToneLock resolves, to avoid flashing all 7 buttons before collapsing to 1 */}
        {isAstriaSpanish && spanishToneLock !== undefined && (
          <div className="max-w-3xl mx-auto mb-2 flex items-center justify-center gap-2">
            {(
              [
                { key: "neutral", label: "Neutral", flag: "🌎" },
                { key: "spain", label: "España", flag: "🇪🇸" },
                { key: "mexico", label: "México", flag: "🇲🇽" },
                { key: "argentina", label: "Argentina", flag: "🇦🇷" },
                { key: "colombia", label: "Colombia", flag: "🇨🇴" },
                { key: "chile", label: "Chile", flag: "🇨🇱" },
                { key: "peru", label: "Perú", flag: "🇵🇪" },
              ] as const
            )
              .filter(({ key }) => !spanishToneLock || spanishToneLock === key)
              .map(({ key, label, flag }) => {
                const isActive = spanishTone === key;
                return (
                  <button
                    key={key}
                    onClick={() => selectSpanishTone(key)}
                    className="px-4 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 border"
                    style={{
                      opacity: isActive ? 1 : 0.5,
                      fontWeight: isActive ? 700 : 500,
                      borderColor: isActive
                        ? SIDEBAR_COLORS.amethyst
                        : `${SIDEBAR_COLORS.roseGold}80`,
                      backgroundColor: isActive
                        ? `${SIDEBAR_COLORS.amethyst}15`
                        : "transparent",
                      color: SIDEBAR_COLORS.darkAmethyst,
                      textDecoration: "none",
                    }}
                  >
                    {flag} {label}
                  </button>
                );
              })}
          </div>
        )}

        {/* Spanish V2 country selector — shares spanishToneLock with the lane above, so picking either locks both */}
        {isAstriaSpanishV2 && spanishToneLock !== undefined && (
          <div className="max-w-3xl mx-auto mb-2 flex items-center justify-center gap-2 flex-wrap">
            {SPANISH_LANE_V4_ORDER.filter(
              (key) => !spanishToneLock || spanishToneLock === key,
            ).map((key) => {
              const country = SPANISH_LANE_V4_COUNTRIES[key];
              const isActive = spanishTone === key;
              const flag = SPANISH_LANE_V4_FLAGS[key] || "🌎";
              return (
                <button
                  key={key}
                  onClick={() => selectSpanishTone(key)}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm transition-all duration-200 border"
                  style={{
                    fontWeight: isActive ? 700 : 500,
                    borderColor: isActive
                      ? country.accentColor
                      : `${country.accentColor}55`,
                    backgroundColor: isActive
                      ? country.accentSoft
                      : "transparent",
                    color: isActive ? country.accentColor : "#5A5A5A",
                  }}
                >
                  <span>{flag}</span>
                  {country.flagLabel}
                </button>
              );
            })}
          </div>
        )}

        {/* Korea Hybrid response-mode toggle — only shown for Astria Korea Hybrid */}
        {isAstriaKoreaHybrid && (
          <div className="max-w-3xl mx-auto mb-2 flex items-center justify-center gap-2">
            {(
              [
                { key: "hybrid", label: "Hybrid" },
                { key: "traditional", label: "Traditional" },
              ] as const
            ).map(({ key, label }) => {
              const isActive = koreaHybridMode === key;
              return (
                <button
                  key={key}
                  onClick={() => setKoreaHybridMode(key)}
                  className="px-4 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 border"
                  style={{
                    opacity: isActive ? 1 : 0.5,
                    fontWeight: isActive ? 700 : 500,
                    borderColor: isActive
                      ? SIDEBAR_COLORS.amethyst
                      : `${SIDEBAR_COLORS.roseGold}80`,
                    backgroundColor: isActive
                      ? `${SIDEBAR_COLORS.amethyst}15`
                      : "transparent",
                    color: SIDEBAR_COLORS.darkAmethyst,
                    textDecoration: "none",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Japan Hybrid response-mode toggle — only shown for Astria Japan Hybrid */}
        {isAstriaJapanHybrid && (
          <div className="max-w-3xl mx-auto mb-2 flex items-center justify-center gap-2">
            {(
              [
                { key: "hybrid", label: "Hybrid" },
                { key: "traditional", label: "Traditional" },
              ] as const
            ).map(({ key, label }) => {
              const isActive = japanHybridMode === key;
              return (
                <button
                  key={key}
                  onClick={() => setJapanHybridMode(key)}
                  className="px-4 py-1.5 rounded-xl text-sm font-medium transition-all duration-200 border"
                  style={{
                    opacity: isActive ? 1 : 0.5,
                    fontWeight: isActive ? 700 : 500,
                    borderColor: isActive
                      ? SIDEBAR_COLORS.amethyst
                      : `${SIDEBAR_COLORS.roseGold}80`,
                    backgroundColor: isActive
                      ? `${SIDEBAR_COLORS.amethyst}15`
                      : "transparent",
                    color: SIDEBAR_COLORS.darkAmethyst,
                    textDecoration: "none",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          <div
            className="relative rounded-2xl border shadow-sm focus-within:ring-2"
            style={{
              borderColor: `${SIDEBAR_COLORS.roseGold}40`,
              backgroundColor: "white",
            }}
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={
                isAuthenticated
                  ? t("chatInterface.typeYourMessage")
                  : t("chatInterface.askMeAnything")
              }
              rows={1}
              disabled={displayLoading}
              className={`w-full resize-none rounded-2xl bg-transparent px-4 py-3 pr-14 text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:opacity-60 leading-6 max-h-40 overflow-y-auto ${
                isElderlyMode ? "text-xl" : ""
              }`}
              style={{ color: SIDEBAR_COLORS.darkAmethyst }}
              autoFocus
            />

            <button
              onClick={handleSend}
              disabled={!input.trim() || displayLoading}
              className={`absolute right-2.5 bottom-2.5 inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed ${
                isElderlyMode ? "healing-button" : ""
              }`}
              style={{
                backgroundColor: SIDEBAR_COLORS.amethyst,
              }}
              aria-label="ส่งข้อความ"
              title={t("chatInterface.send")}
            >
              {displayLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>

          <div
            className="mt-2 text-center text-[11px]"
            style={{ color: SIDEBAR_COLORS.darkAmethyst }}
          >
            {displayTyping ? (
              <span
                className="inline-flex items-center justify-center gap-2"
                style={{ color: SIDEBAR_COLORS.amethyst }}
              >
                {astriaAILabel ?? t("chatInterface.pressEnterToSend")}
              </span>
            ) : (
              <span>
                {astriaAILabel ?? t("chatInterface.pressEnterToSend")}
              </span>
            )}
          </div>
        </div>
      </div>

      <BirthDetailsPopup
        isOpen={isBirthDetailsPopupOpen}
        onClose={() => setIsBirthDetailsPopupOpen(false)}
        onSave={handleBirthDetailsSave}
        category={
          subCategories.find((s) => s.id === currentChat?.subCategoryId)?.name
        }
        mainCategory={
          activeCategories.find((c) => c.id === currentChat?.categoryId)?.name
        }
      />

      {isAstriaGCCBrand && (
        <GCCPrivacyPopup
          isOpen={showGCCPrivacyPopup}
          onConfirm={handleGCCPrivacyPopupConfirm}
        />
      )}
    </div>
  );
};

export default ChatInterface;
